import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ColorPicker from '@rc-component/color-picker';
import '@rc-component/color-picker/assets/index.css';
import { BiSolidMessageSquareDetail } from 'react-icons/bi';
import { TbSend } from 'react-icons/tb';
import { ChatHeader, ChatFooter } from '@kichatorg/chatui-components';
import FloatingLinesPreview from './FloatingLinesPreview';
import { notifyError, notifySuccess } from '../utils/statusShortcuts';
import './ChatBuilderEditor.css';

const STORAGE_PREFIX = 'kichat:chat-ui-config:v1:';

const INSPECTOR_SECTIONS = [
  { id: 'global', label: 'Globalt' },
  { id: 'header', label: 'Header' },
  { id: 'home', label: 'Hjem' },
  { id: 'footer', label: 'Footer' },
  { id: 'advanced', label: 'Avansert' },
];

const safeJsonParse = (value) => {
  if (!value || typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const normalizeHex = (value, fallback = '#3451ff') => {
  if (!value || typeof value !== 'string') return fallback;
  const trimmed = value.trim().replace(/^#/, '');
  if (/^[0-9a-fA-F]{6}$/.test(trimmed)) return `#${trimmed.toLowerCase()}`;
  if (/^[0-9a-fA-F]{3}$/.test(trimmed)) {
    const expanded = trimmed.split('').map((c) => c + c).join('');
    return `#${expanded.toLowerCase()}`;
  }
  return fallback;
};

const hexToRgb = (hex) => {
  const normalized = normalizeHex(hex, '#ffffff');
  return {
    r: parseInt(normalized.slice(1, 3), 16),
    g: parseInt(normalized.slice(3, 5), 16),
    b: parseInt(normalized.slice(5, 7), 16),
  };
};

const withAlpha = (hex, alpha) => {
  const { r, g, b } = hexToRgb(hex);
  const a = clampNumber(alpha, 0, 1, 1);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const getLuminance = (hex) => {
  const { r, g, b } = hexToRgb(hex);
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

const getReadableTextColor = (bgHex) => (getLuminance(bgHex) > 0.4 ? '#0f172a' : '#ffffff');

const getContrastRatio = (bgHex, textHex) => {
  const L1 = getLuminance(bgHex);
  const L2 = getLuminance(textHex);
  const lighter = Math.max(L1, L2);
  const darker = Math.min(L1, L2);
  return (lighter + 0.05) / (darker + 0.05);
};

const resolvePreferredTextColor = (bgHex, preferredHex, { minContrast = 4.5 } = {}) => {
  const fallback = getReadableTextColor(bgHex);
  if (!preferredHex) return fallback;
  const pref = normalizeHex(preferredHex, fallback);
  return getContrastRatio(bgHex, pref) >= minContrast ? pref : fallback;
};

const clampNumber = (value, min, max, fallback) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(Math.max(parsed, min), max);
};

const DEFAULT_FONT_FAMILY =
  "Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const FONT_PRESETS = [
  { id: 'default', label: 'Standard', value: DEFAULT_FONT_FAMILY },
  {
    id: 'system',
    label: 'System UI',
    value: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  { id: 'roboto', label: 'Roboto', value: 'Roboto, system-ui, -apple-system, "Segoe UI", "Helvetica Neue", Arial, sans-serif' },
  {
    id: 'helvetica-neue',
    label: 'Helvetica Neue',
    value: '"Helvetica Neue", Helvetica, Arial, system-ui, -apple-system, "Segoe UI", sans-serif',
  },
  { id: 'arial', label: 'Arial', value: 'Arial, system-ui, -apple-system, "Segoe UI", sans-serif' },
];

const FONT_VARIANT_NUMERIC_TOKENS = new Set([
  'normal',
  'lining-nums',
  'oldstyle-nums',
  'proportional-nums',
  'tabular-nums',
  'diagonal-fractions',
  'stacked-fractions',
  'ordinal',
  'slashed-zero',
]);

const normalizeFontFamily = (value, fallback = DEFAULT_FONT_FAMILY) => {
  if (typeof value !== 'string') return fallback;
  const trimmed = value.trim();
  return trimmed ? trimmed : fallback;
};

const normalizeFontVariantNumeric = (value, fallback = 'normal') => {
  if (typeof value !== 'string') return fallback;
  const tokens = value
    .split(/\s+/)
    .map((t) => t.trim())
    .filter(Boolean)
    .filter((t) => FONT_VARIANT_NUMERIC_TOKENS.has(t));
  const unique = Array.from(new Set(tokens));
  if (unique.length === 0) return fallback;
  const withoutNormal = unique.filter((t) => t !== 'normal');
  return withoutNormal.length ? withoutNormal.join(' ') : 'normal';
};

const MAX_CUSTOM_SHAPE_POINTS = 200;

const normalizeHeaderShapeCustom = (candidateCustom, fallbackCustom) => {
  const candidate = candidateCustom && typeof candidateCustom === 'object' ? candidateCustom : {};
  const fallback = fallbackCustom && typeof fallbackCustom === 'object' ? fallbackCustom : {};
  const fallbackMode = fallback.mode === 'raw' || fallback.mode === 'points' ? fallback.mode : 'points';
  const rawMode = typeof candidate.mode === 'string' ? candidate.mode : fallbackMode;
  const mode = rawMode === 'raw' || rawMode === 'points' ? rawMode : 'points';

  const mirror = candidate.mirror !== undefined ? Boolean(candidate.mirror) : Boolean(fallback.mirror);
  const roundingSegments = clampNumber(
    candidate.roundingSegments,
    2,
    16,
    clampNumber(fallback.roundingSegments, 2, 16, 6),
  );

  const fallbackPoints = Array.isArray(fallback.points) ? fallback.points : [];
  const rawPoints = Array.isArray(candidate.points) ? candidate.points : fallbackPoints;
  const maxX = mirror ? 50 : 100;

  const normalizePoint = (point, index) => {
    const safe = point && typeof point === 'object' ? point : {};
    const fallbackPoint = fallbackPoints[index] && typeof fallbackPoints[index] === 'object' ? fallbackPoints[index] : {};
    const rawId = typeof safe.id === 'string' && safe.id.trim() ? safe.id.trim() : fallbackPoint.id;
    const id = typeof rawId === 'string' && rawId.trim() ? rawId.trim() : `p-${index}`;

    return {
      id,
      x: clampNumber(safe.x, 0, maxX, clampNumber(fallbackPoint.x, 0, maxX, 0)),
      y: clampNumber(safe.y, 0, 100, clampNumber(fallbackPoint.y, 0, 100, 100)),
      radius: clampNumber(safe.radius, 0, CUSTOM_MAX_POINT_RADIUS, clampNumber(fallbackPoint.radius, 0, CUSTOM_MAX_POINT_RADIUS, 0)),
    };
  };

  let points = rawPoints.map(normalizePoint);
  const endX = mirror ? 50 : 100;

  const ensurePointAtX = (list, x, endpointId) => {
    const EPS = 0.0001;
    const byIdIndex = list.findIndex((p) => p.id === endpointId);
    const byXIndex = list.findIndex((p) => Math.abs(p.x - x) < EPS);
    const index = byIdIndex >= 0 ? byIdIndex : byXIndex;
    if (index >= 0) {
      return list.map((p, idx) => (idx === index ? { ...p, x } : p));
    }
    return [...list, { id: endpointId, x, y: 100, radius: 0 }];
  };

  points = ensurePointAtX(points, 0, 'p-left');
  points = ensurePointAtX(points, endX, mirror ? 'p-center' : 'p-right');

  points = points
    .map((p) => {
      const x = Math.abs(p.x) < 0.0001 ? 0 : Math.abs(p.x - endX) < 0.0001 ? endX : p.x;
      return { ...p, x };
    })
    .sort((a, b) => a.x - b.x)
    .slice(0, MAX_CUSTOM_SHAPE_POINTS);

  return { mode, mirror, roundingSegments, points };
};

const buildDefaultConfig = (bot) => {
  const accent = normalizeHex(bot?.color || '#3451ff');
  const start = normalizeHex(bot?.gradientStart || bot?.color || '#1e255d', accent);
  const end = normalizeHex(bot?.gradientEnd || bot?.color || '#2a9d8f', accent);
  const titleFallback = bot?.botTitle || bot?.name || 'Kundeservice';
  const hasBotLogo = Boolean(bot?.logoPreviewUrl);

  return {
    version: 1,
    header: {
      template: 'classic',
      height: 56,
      home: {
        mode: 'hero',
        background: 'colored',
        align: 'left',
        content: hasBotLogo ? 'logo' : 'title',
        logoSize: 80,
      },
      compact: {
        content: hasBotLogo ? 'both' : 'title',
      },
      shape: {
        type: 'straight',
        radius: 18,
        angleCut: 14,
        angleDirection: 'right',
        waveAmplitude: 14,
        waveFrequency: 2,
        clipPath: '',
        custom: {
          mode: 'points',
          mirror: true,
          roundingSegments: 6,
          points: [
            { id: 'p-left', x: 0, y: 100, radius: 0 },
            { id: 'p-center', x: 50, y: 100, radius: 0 },
          ],
        },
      },
      background: {
        type: 'solid',
        color: accent,
        gradientStart: start,
        gradientEnd: end,
        angle: 135,
      },
      border: {
        enabled: false,
        color: 'rgba(255,255,255,0.12)',
      },
      logo: {
        mode: hasBotLogo ? 'botLogo' : 'none',
        url: '',
        size: 28,
        cornerRadius: 10,
      },
      title: {
        text: titleFallback,
      },
      subtitle: {
        enabled: false,
        text: '',
      },
      actions: {
        expand: true,
        close: true,
      },
    },
    theme: {
      accent,
      presetId: typeof bot?.themePreset === 'string' && bot.themePreset.trim() ? bot.themePreset.trim() : 'custom',
      locked: Boolean(bot?.themePreset && bot.themePreset !== 'custom'),
      footer: {
        style: 'minimal', // 'minimal' | 'dock'
        nav: {
          mode: 'glass', // 'glass' | 'solid' | 'gradient'
          tint: '#111827',
          opacity: 0.72,
          color: '#111827',
          gradientStart: '#111827',
          gradientEnd: '#0b1020',
          angle: 135,
        },
        background: {
          mode: 'fade', // 'fade' | 'solid' | 'gradient' | 'none'
          color: accent,
          gradientStart: accent,
          gradientEnd: end,
          angle: 135,
        },
      },
      typography: {
        fontFamily: DEFAULT_FONT_FAMILY,
        fontVariantNumeric: 'normal',
        textColor: '#ffffff',
      },
      background: { type: 'gradient', start, end, angle: 135, effect: 'none' },
      surfaces: {
        card: normalizeHex(bot?.launcherColor || bot?.gradientStart || accent, accent),
        input: normalizeHex(bot?.inputBgColor || bot?.launcherColor || '#f4f4f5', '#f4f4f5'),
      },
    },
  };
};

const mergeWithDefaults = (defaults, candidate) => {
  if (!candidate || typeof candidate !== 'object') return defaults;
  const next = { ...defaults, ...candidate };
  const candidateHeader = candidate.header && typeof candidate.header === 'object' ? candidate.header : {};
  const candidateHeaderHome =
    candidateHeader.home && typeof candidateHeader.home === 'object' ? candidateHeader.home : {};
  const candidateHeaderCompact =
    candidateHeader.compact && typeof candidateHeader.compact === 'object' ? candidateHeader.compact : {};
  const candidateHeaderShape =
    candidateHeader.shape && typeof candidateHeader.shape === 'object' ? candidateHeader.shape : {};
  const candidateHeaderBg =
    candidateHeader.background && typeof candidateHeader.background === 'object'
      ? candidateHeader.background
      : {};
  const candidateHeaderBorder =
    candidateHeader.border && typeof candidateHeader.border === 'object' ? candidateHeader.border : {};
  const candidateHeaderLogo =
    candidateHeader.logo && typeof candidateHeader.logo === 'object' ? candidateHeader.logo : {};
  const candidateHeaderTitle =
    candidateHeader.title && typeof candidateHeader.title === 'object' ? candidateHeader.title : {};
  const candidateHeaderSubtitle =
    candidateHeader.subtitle && typeof candidateHeader.subtitle === 'object' ? candidateHeader.subtitle : {};

  const candidateHeaderShapeCustom =
    candidateHeaderShape.custom && typeof candidateHeaderShape.custom === 'object'
      ? candidateHeaderShape.custom
      : {};
  const defaultHeaderShapeCustom =
    defaults.header?.shape?.custom && typeof defaults.header.shape.custom === 'object'
      ? defaults.header.shape.custom
      : {};
  const rawCustomMode =
    typeof candidateHeaderShapeCustom.mode === 'string'
      ? candidateHeaderShapeCustom.mode
      : defaultHeaderShapeCustom.mode;
  const customMode = rawCustomMode === 'raw' || rawCustomMode === 'points' ? rawCustomMode : 'points';
  const customMirror =
    candidateHeaderShapeCustom.mirror !== undefined
      ? Boolean(candidateHeaderShapeCustom.mirror)
      : Boolean(defaultHeaderShapeCustom.mirror);
  const customRoundingSegments = clampNumber(
    candidateHeaderShapeCustom.roundingSegments,
    2,
    16,
    defaultHeaderShapeCustom.roundingSegments ?? 6,
  );
  const rawCustomPoints = Array.isArray(candidateHeaderShapeCustom.points)
    ? candidateHeaderShapeCustom.points
    : Array.isArray(defaultHeaderShapeCustom.points)
      ? defaultHeaderShapeCustom.points
      : [];

  const normalizeCustomPoint = (point, index) => {
    const safe = point && typeof point === 'object' ? point : {};
    const fallback = Array.isArray(defaultHeaderShapeCustom.points) ? defaultHeaderShapeCustom.points[index] : null;
    const fallbackSafe = fallback && typeof fallback === 'object' ? fallback : {};
    const id = typeof safe.id === 'string' && safe.id.trim() ? safe.id.trim() : (fallbackSafe.id || `p-${index}`);
    const maxX = customMirror ? 50 : 100;
    return {
      id,
      x: clampNumber(safe.x, 0, maxX, clampNumber(fallbackSafe.x, 0, maxX, 0)),
      y: clampNumber(safe.y, 0, 100, clampNumber(fallbackSafe.y, 0, 100, 100)),
      radius: clampNumber(safe.radius, 0, CUSTOM_MAX_POINT_RADIUS, clampNumber(fallbackSafe.radius, 0, CUSTOM_MAX_POINT_RADIUS, 0)),
    };
  };

  const ensureCustomPointAt = (points, x, defaultY = 100) => {
    const existing = points.find((p) => Math.abs(p.x - x) < 0.0001);
    if (existing) {
      return points.map((p) => (p.id === existing.id ? { ...p, x } : p));
    }
    return [
      ...points,
      { id: x === 0 ? 'p-left' : customMirror ? 'p-center' : 'p-right', x, y: defaultY, radius: 0 },
    ];
  };

  let normalizedCustomPoints = rawCustomPoints.map(normalizeCustomPoint);
  normalizedCustomPoints = ensureCustomPointAt(normalizedCustomPoints, 0, 100);
  normalizedCustomPoints = ensureCustomPointAt(normalizedCustomPoints, customMirror ? 50 : 100, 100);
  normalizedCustomPoints = normalizedCustomPoints
    .map((p) => ({ ...p, x: p.x === 0 ? 0 : p.x === (customMirror ? 50 : 100) ? (customMirror ? 50 : 100) : p.x }))
    .sort((a, b) => a.x - b.x)
    .slice(0, 200);

  next.header = {
    ...defaults.header,
    ...candidateHeader,
    height: clampNumber(candidateHeader.height, 40, 96, defaults.header.height),
    home: {
      ...(defaults.header.home || {}),
      ...candidateHeaderHome,
      mode: candidateHeaderHome.mode === 'compact' ? 'compact' : 'hero',
      background: candidateHeaderHome.background === 'transparent' ? 'transparent' : 'colored',
      align: candidateHeaderHome.align === 'center' ? 'center' : 'left',
      content:
        candidateHeaderHome.content === 'title' || candidateHeaderHome.content === 'both' || candidateHeaderHome.content === 'logo'
          ? candidateHeaderHome.content
          : (defaults.header.home?.content || 'logo'),
      logoSize: clampNumber(candidateHeaderHome.logoSize, 24, 160, defaults.header.home?.logoSize ?? 80),
    },
    compact: {
      ...(defaults.header.compact || {}),
      ...candidateHeaderCompact,
      content:
        candidateHeaderCompact.content === 'title'
        || candidateHeaderCompact.content === 'both'
        || candidateHeaderCompact.content === 'logo'
          ? candidateHeaderCompact.content
          : (defaults.header.compact?.content || 'both'),
    },
    shape: {
      ...defaults.header.shape,
      ...candidateHeaderShape,
      type: ['straight', 'rounded', 'angled', 'wave', 'custom'].includes(candidateHeaderShape.type)
        ? candidateHeaderShape.type
        : defaults.header.shape.type,
      radius: clampNumber(candidateHeaderShape.radius, 0, 48, defaults.header.shape.radius),
      angleCut: clampNumber(candidateHeaderShape.angleCut, 0, 48, defaults.header.shape.angleCut),
      angleDirection: candidateHeaderShape.angleDirection === 'left' ? 'left' : 'right',
      waveAmplitude: clampNumber(candidateHeaderShape.waveAmplitude, 0, 35, defaults.header.shape.waveAmplitude),
      waveFrequency: clampNumber(candidateHeaderShape.waveFrequency, 1, 6, defaults.header.shape.waveFrequency),
      clipPath:
        typeof candidateHeaderShape.clipPath === 'string'
          ? candidateHeaderShape.clipPath
          : defaults.header.shape.clipPath,
      custom: {
        ...defaultHeaderShapeCustom,
        ...candidateHeaderShapeCustom,
        mode: customMode,
        mirror: customMirror,
        roundingSegments: customRoundingSegments,
        points: normalizedCustomPoints,
      },
    },
    background: {
      ...defaults.header.background,
      ...candidateHeaderBg,
      type: candidateHeaderBg.type === 'gradient' ? 'gradient' : 'solid',
      color: normalizeHex(candidateHeaderBg.color, defaults.header.background.color),
      gradientStart: normalizeHex(candidateHeaderBg.gradientStart, defaults.header.background.gradientStart),
      gradientEnd: normalizeHex(candidateHeaderBg.gradientEnd, defaults.header.background.gradientEnd),
      angle: clampNumber(candidateHeaderBg.angle, 0, 360, defaults.header.background.angle),
    },
    border: {
      ...defaults.header.border,
      ...candidateHeaderBorder,
      enabled: Boolean(candidateHeaderBorder.enabled),
      color: typeof candidateHeaderBorder.color === 'string' ? candidateHeaderBorder.color : defaults.header.border.color,
    },
    logo: {
      ...defaults.header.logo,
      ...candidateHeaderLogo,
      mode:
        candidateHeaderLogo.mode === 'botLogo'
        || candidateHeaderLogo.mode === 'url'
        || candidateHeaderLogo.mode === 'none'
          ? candidateHeaderLogo.mode
          : defaults.header.logo.mode,
      url: typeof candidateHeaderLogo.url === 'string' ? candidateHeaderLogo.url : defaults.header.logo.url,
      size: clampNumber(candidateHeaderLogo.size, 16, 72, defaults.header.logo.size),
      cornerRadius: clampNumber(candidateHeaderLogo.cornerRadius, 0, 24, defaults.header.logo.cornerRadius),
    },
    title: {
      ...defaults.header.title,
      ...candidateHeaderTitle,
      text: typeof candidateHeaderTitle.text === 'string' ? candidateHeaderTitle.text : defaults.header.title.text,
    },
    subtitle: {
      ...defaults.header.subtitle,
      ...candidateHeaderSubtitle,
      enabled: Boolean(candidateHeaderSubtitle.enabled),
      text:
        typeof candidateHeaderSubtitle.text === 'string'
          ? candidateHeaderSubtitle.text
          : defaults.header.subtitle.text,
    },
    actions: {
      expand: true,
      close: true,
    },
  };

  const candidateTheme = candidate.theme && typeof candidate.theme === 'object' ? candidate.theme : {};
  const candidateThemeBg =
    candidateTheme.background && typeof candidateTheme.background === 'object' ? candidateTheme.background : {};
  const candidateThemeSurfacesRaw =
    candidateTheme.surfaces && typeof candidateTheme.surfaces === 'object' ? candidateTheme.surfaces : {};
  // Panel is deprecated/removed from the dashboard design config.
  const { panel: _deprecatedPanelSurface, ...candidateThemeSurfaces } = candidateThemeSurfacesRaw;
  const candidateThemeFooter =
    candidateTheme.footer && typeof candidateTheme.footer === 'object' ? candidateTheme.footer : {};
  const candidateThemeFooterNav =
    candidateThemeFooter.nav && typeof candidateThemeFooter.nav === 'object' ? candidateThemeFooter.nav : {};
  const candidateThemeFooterBg =
    candidateThemeFooter.background && typeof candidateThemeFooter.background === 'object' ? candidateThemeFooter.background : {};
  const candidateThemeTypography =
    candidateTheme.typography && typeof candidateTheme.typography === 'object' ? candidateTheme.typography : {};
  const candidateThemePresetId = typeof candidateTheme.presetId === 'string' ? candidateTheme.presetId.trim() : '';
  const defaultThemePresetId =
    typeof defaults.theme?.presetId === 'string' && defaults.theme.presetId.trim()
      ? defaults.theme.presetId.trim()
      : 'custom';
  const mergedThemePresetId = candidateThemePresetId || defaultThemePresetId;

  next.theme = {
    ...defaults.theme,
    ...candidateTheme,
    accent: normalizeHex(candidateTheme.accent, defaults.theme.accent),
    presetId: mergedThemePresetId,
    locked: candidateTheme.locked !== undefined ? Boolean(candidateTheme.locked) : Boolean(defaults.theme.locked),
    footer: {
      ...(defaults.theme.footer || {}),
      ...candidateThemeFooter,
      style: candidateThemeFooter.style === 'dock' ? 'dock' : 'minimal',
      nav: {
        ...(defaults.theme.footer?.nav || {}),
        ...candidateThemeFooterNav,
        mode:
          candidateThemeFooterNav.mode === 'solid' || candidateThemeFooterNav.mode === 'gradient' || candidateThemeFooterNav.mode === 'glass'
            ? candidateThemeFooterNav.mode
            : (defaults.theme.footer?.nav?.mode || 'glass'),
        tint: normalizeHex(candidateThemeFooterNav.tint, defaults.theme.footer?.nav?.tint || '#111827'),
        opacity: clampNumber(candidateThemeFooterNav.opacity, 0, 0.92, defaults.theme.footer?.nav?.opacity ?? 0.72),
        color: normalizeHex(candidateThemeFooterNav.color, defaults.theme.footer?.nav?.color || '#111827'),
        gradientStart: normalizeHex(candidateThemeFooterNav.gradientStart, defaults.theme.footer?.nav?.gradientStart || '#111827'),
        gradientEnd: normalizeHex(candidateThemeFooterNav.gradientEnd, defaults.theme.footer?.nav?.gradientEnd || '#0b1020'),
        angle: clampNumber(candidateThemeFooterNav.angle, 0, 360, defaults.theme.footer?.nav?.angle ?? 135),
      },
        background: {
          ...(defaults.theme.footer?.background || {}),
          ...candidateThemeFooterBg,
          mode:
            candidateThemeFooterBg.mode === 'solid'
            || candidateThemeFooterBg.mode === 'gradient'
            || candidateThemeFooterBg.mode === 'fade'
            || candidateThemeFooterBg.mode === 'none'
              ? candidateThemeFooterBg.mode
              : (defaults.theme.footer?.background?.mode || 'fade'),
          color: normalizeHex(candidateThemeFooterBg.color, defaults.theme.footer?.background?.color || defaults.theme.accent),
          gradientStart: normalizeHex(candidateThemeFooterBg.gradientStart, defaults.theme.footer?.background?.gradientStart || defaults.theme.accent),
          gradientEnd: normalizeHex(candidateThemeFooterBg.gradientEnd, defaults.theme.footer?.background?.gradientEnd || defaults.theme.background.end),
          angle: clampNumber(candidateThemeFooterBg.angle, 0, 360, defaults.theme.footer?.background?.angle ?? 135),
        },
    },
    typography: {
      ...(defaults.theme.typography || {}),
      ...candidateThemeTypography,
      fontFamily: normalizeFontFamily(
        candidateThemeTypography.fontFamily,
        defaults.theme.typography?.fontFamily || DEFAULT_FONT_FAMILY,
      ),
      fontVariantNumeric: normalizeFontVariantNumeric(
        candidateThemeTypography.fontVariantNumeric,
        defaults.theme.typography?.fontVariantNumeric || 'normal',
      ),
      textColor: normalizeHex(candidateThemeTypography.textColor, defaults.theme.typography?.textColor || '#ffffff'),
    },
    background: {
      ...defaults.theme.background,
      ...candidateThemeBg,
      type: candidateThemeBg.type === 'solid' ? 'solid' : 'gradient',
      start: normalizeHex(candidateThemeBg.start, defaults.theme.background.start),
      end: normalizeHex(candidateThemeBg.end, defaults.theme.background.end),
      angle: clampNumber(candidateThemeBg.angle, 0, 360, defaults.theme.background.angle),
      effect:
        typeof candidateThemeBg.effect === 'string'
          ? candidateThemeBg.effect
          : (defaults.theme.background.effect || 'none'),
    },
    surfaces: {
      card: normalizeHex(candidateThemeSurfaces.card, defaults.theme.surfaces.card),
      input: normalizeHex(candidateThemeSurfaces.input, defaults.theme.surfaces.input),
    },
  };

  return next;
};

const resolveHeaderBackgroundStyle = (headerBg) => {
  if (headerBg?.type === 'gradient') {
    return {
      backgroundImage: `linear-gradient(${headerBg.angle || 135}deg, ${headerBg.gradientStart}, ${headerBg.gradientEnd})`,
      backgroundColor: headerBg.gradientStart,
    };
  }
  return { backgroundColor: headerBg.color };
};

const buildAngledClipPath = (cutPercent, direction) => {
  const cut = clampNumber(cutPercent, 0, 48, 14);
  const y = Math.max(0, Math.min(100, 100 - cut));
  if (direction === 'left') {
    return `polygon(0% 0%, 100% 0%, 100% 100%, 0% ${y}%)`;
  }
  return `polygon(0% 0%, 100% 0%, 100% ${y}%, 0% 100%)`;
};

const buildWaveClipPath = (amplitudePercent, frequency, samples = 28) => {
  const amplitude = clampNumber(amplitudePercent, 0, 35, 14);
  const freq = clampNumber(frequency, 1, 6, 2);
  const count = clampNumber(samples, 8, 80, 28);

  const phase = -Math.PI / 2;
  const points = [];
  for (let i = 0; i <= count; i += 1) {
    const t = i / count;
    const x = 100 - t * 100;
    const sine = Math.sin(2 * Math.PI * freq * t + phase);
    const wave = (sine + 1) / 2;
    const y = 100 - amplitude * wave;
    points.push(`${x.toFixed(2)}% ${y.toFixed(2)}%`);
  }
  return `polygon(0% 0%, 100% 0%, ${points.join(', ')})`;
};

const createCustomPointId = () =>
  `p-${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;

const clampPercent = (value, fallback) => clampNumber(value, 0, 100, fallback);

const CUSTOM_EDGE_GUTTER = 0.6; // prevents "stuck" points on endpoints
const CUSTOM_MAX_POINT_RADIUS = 80;

const isCustomEndpointId = (id, mirror) => {
  const safe = String(id || '');
  return safe === 'p-left' || safe === (mirror ? 'p-center' : 'p-right');
};

const formatPercent = (value) => {
  const clamped = clampPercent(value, 0);
  const fixed = clamped.toFixed(2);
  return fixed.replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
};

const buildCustomFullPoints = (custom) => {
  const safe = custom && typeof custom === 'object' ? custom : {};
  const mirror = Boolean(safe.mirror);
  const rawPoints = Array.isArray(safe.points) ? safe.points : [];

  const points = rawPoints
    .map((point, index) => {
      const p = point && typeof point === 'object' ? point : {};
      const xMax = mirror ? 50 : 100;
      return {
        id: typeof p.id === 'string' && p.id.trim() ? p.id.trim() : `p-${index}`,
        x: clampNumber(p.x, 0, xMax, 0),
        y: clampPercent(p.y, 100),
        radius: clampNumber(p.radius, 0, CUSTOM_MAX_POINT_RADIUS, 0),
      };
    })
    .sort((a, b) => a.x - b.x);

  if (!mirror) return points;

  const EPS = 0.0001;
  const mirrored = points
    .filter((p) => Math.abs(p.x - 50) > EPS)
    .map((p) => ({
      ...p,
      id: `${p.id}::mirror`,
      x: 100 - p.x,
    }));

  return [...points, ...mirrored].sort((a, b) => a.x - b.x);
};

const normalizeAngleDelta = (delta) => {
  const twoPi = Math.PI * 2;
  const normalized = ((delta + Math.PI) % twoPi + twoPi) % twoPi - Math.PI;
  return normalized;
};

/**
 * Fillet corners (true corner radius) by inserting arc points at each vertex.
 * This keeps edges straight and rounds only at the corner, allowing "round but steep" angles.
 */
const filletPolygon = (vertices, radii, segments) => {
  const count = Array.isArray(vertices) ? vertices.length : 0;
  if (count < 3) return vertices;
  const steps = clampNumber(segments, 2, 16, 6);
  const result = [];
  const twoPi = Math.PI * 2;

  const last = () => (result.length ? result[result.length - 1] : null);
  const pushIfDistinct = (point) => {
    const prev = last();
    if (!prev) {
      result.push(point);
      return;
    }
    const dx = prev.x - point.x;
    const dy = prev.y - point.y;
    if (Math.hypot(dx, dy) > 0.001) {
      result.push(point);
    }
  };

  for (let i = 0; i < count; i += 1) {
    const prev = vertices[(i - 1 + count) % count];
    const curr = vertices[i];
    const next = vertices[(i + 1) % count];
    // "radius" is treated as a local rounding amount.
    // We cap how far the fillet can travel along each edge to keep rounding isolated.
    const requestedRadius = clampNumber(radii?.[i], 0, CUSTOM_MAX_POINT_RADIUS, 0);

    if (!requestedRadius) {
      pushIfDistinct(curr);
      continue;
    }

    // Directions from curr toward prev/next
    const a = { x: prev.x - curr.x, y: prev.y - curr.y };
    const b = { x: next.x - curr.x, y: next.y - curr.y };
    const lenA = Math.hypot(a.x, a.y);
    const lenB = Math.hypot(b.x, b.y);
    if (!lenA || !lenB) {
      pushIfDistinct(curr);
      continue;
    }

    const u1 = { x: a.x / lenA, y: a.y / lenA };
    const u2 = { x: b.x / lenB, y: b.y / lenB };

    // Angle between edges
    const dot = Math.min(1, Math.max(-1, u1.x * u2.x + u1.y * u2.y));
    const theta = Math.acos(dot);
    if (!Number.isFinite(theta) || theta < 0.001 || Math.abs(Math.PI - theta) < 0.001) {
      pushIfDistinct(curr);
      continue;
    }

    // Convert radius -> tangent distance along each edge.
    const tanHalf = Math.tan(theta / 2);
    if (!Number.isFinite(tanHalf) || Math.abs(tanHalf) < 0.00001) {
      pushIfDistinct(curr);
      continue;
    }
    const tWanted = requestedRadius / tanHalf;
    // Key tweak: keep rounding "local" by limiting edge-travel.
    const tCap = requestedRadius;
    const t = Math.max(0, Math.min(tWanted, tCap, lenA - 0.001, lenB - 0.001));
    if (t <= 0.001) {
      pushIfDistinct(curr);
      continue;
    }
    const radius = Math.max(0, Math.min(requestedRadius, t * tanHalf));

    const start = { x: curr.x + u1.x * t, y: curr.y + u1.y * t };
    const end = { x: curr.x + u2.x * t, y: curr.y + u2.y * t };

    // Circle center on angle bisector
    const bis = { x: u1.x + u2.x, y: u1.y + u2.y };
    const bisLen = Math.hypot(bis.x, bis.y);
    if (bisLen <= 0.00001) {
      pushIfDistinct(curr);
      continue;
    }
    const bisUnit = { x: bis.x / bisLen, y: bis.y / bisLen };
    const sinHalf = Math.sin(theta / 2);
    if (!Number.isFinite(sinHalf) || Math.abs(sinHalf) < 0.00001) {
      pushIfDistinct(curr);
      continue;
    }
    const h = radius / sinHalf;
    const center = { x: curr.x + bisUnit.x * h, y: curr.y + bisUnit.y * h };

    const a1 = Math.atan2(start.y - center.y, start.x - center.x);
    const a2 = Math.atan2(end.y - center.y, end.x - center.x);
    // shortest arc (fillet), but still ensure it's bounded.
    let delta = normalizeAngleDelta(a2 - a1);
    if (delta > Math.PI) delta -= twoPi;
    if (delta < -Math.PI) delta += twoPi;

    pushIfDistinct(start);
    for (let step = 1; step < steps; step += 1) {
      const tt = step / steps;
      const angle = a1 + delta * tt;
      pushIfDistinct({
        x: center.x + Math.cos(angle) * radius,
        y: center.y + Math.sin(angle) * radius,
      });
    }
    pushIfDistinct(end);
  }

  return result;
};

const buildCustomClipPath = (custom, aspectRatio = 1) => {
  const safe = custom && typeof custom === 'object' ? custom : {};
  const fullPoints = buildCustomFullPoints(safe);
  if (fullPoints.length < 2) return '';

  const aspect = clampNumber(aspectRatio, 0.5, 20, 1);
  const sorted = fullPoints
    .map((p) => ({
      x: clampPercent(p.x, 0),
      y: clampPercent(p.y, 100),
      radius: clampNumber(p.radius, 0, CUSTOM_MAX_POINT_RADIUS, 0),
    }))
    .sort((a, b) => a.x - b.x);

  const bottomVertices = sorted.slice().reverse();
  const vertices = [
    { x: 0, y: 0 },
    { x: 100, y: 0 },
    ...bottomVertices.map((p) => ({ x: p.x, y: p.y })),
  ];
  const radii = [0, 0, ...bottomVertices.map((p) => p.radius)];
  // Correct for percent coordinate anisotropy: x% uses width, y% uses height.
  // We scale X by (width/height) to perform rounding in an isotropic space.
  const scaledVertices = vertices.map((p) => ({ x: p.x * aspect, y: p.y }));
  const roundedScaled = filletPolygon(scaledVertices, radii, safe.roundingSegments);
  const rounded = roundedScaled.map((p) => ({ x: p.x / aspect, y: p.y }));

  const formatted = rounded.map((p) => `${formatPercent(p.x)}% ${formatPercent(p.y)}%`);
  return `polygon(${formatted.join(', ')})`;
};

const resolveHeaderShapeStyle = (shape) => {
  if (!shape || typeof shape !== 'object') return {};

  if (shape.type === 'rounded') {
    const radius = clampNumber(shape.radius, 0, 48, 18);
    return { borderRadius: `0 0 ${radius}px ${radius}px` };
  }

  if (shape.type === 'angled') {
    return { clipPath: buildAngledClipPath(shape.angleCut, shape.angleDirection) };
  }

  if (shape.type === 'wave') {
    return { clipPath: buildWaveClipPath(shape.waveAmplitude, shape.waveFrequency) };
  }

  if (shape.type === 'custom') {
    const customMode = shape.custom?.mode === 'raw' ? 'raw' : 'points';
    const computed = customMode === 'raw' ? '' : buildCustomClipPath(shape.custom);
    const clipPath =
      computed || (typeof shape.clipPath === 'string' ? shape.clipPath.trim() : '');
    return clipPath ? { clipPath } : {};
  }

  return {};
};

const resolveThemeBackgroundStyle = (themeBackground) => {
  if (!themeBackground || typeof themeBackground !== 'object') {
    return { backgroundColor: '#0b1020' };
  }
  const start = normalizeHex(themeBackground.start, '#0b1020');
  const end = normalizeHex(themeBackground.end, start);
  const angle = clampNumber(themeBackground.angle, 0, 360, 135);

  if (String(themeBackground.effect || '') === 'floating-lines') {
    return { backgroundColor: '#050a14' };
  }

  if (themeBackground.type === 'solid') {
    return { backgroundColor: start };
  }

  return {
    backgroundImage: `linear-gradient(${angle}deg, ${start}, ${end})`,
    backgroundColor: start,
  };
};

const getStorageKey = (botId) => `${STORAGE_PREFIX}${String(botId)}`;

const copyTextToClipboard = async (text) => {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }
  if (typeof document === 'undefined') return false;
  const el = document.createElement('textarea');
  el.value = text;
  el.setAttribute('readonly', 'true');
  el.style.position = 'absolute';
  el.style.left = '-9999px';
  document.body.appendChild(el);
  el.select();
  try {
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    return ok;
  } catch {
    document.body.removeChild(el);
    return false;
  }
};

const InlineColorField = ({ label, value, onChange, disabled = false }) => {
  const displayValue = normalizeHex(value);
  const [draft, setDraft] = useState(displayValue);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      setDraft(displayValue);
    }
  }, [displayValue, isFocused]);

  const normalizeIfValid = (input) => {
    if (typeof input !== 'string') return null;
    const trimmed = input.trim();
    if (!trimmed) return null;
    const raw = trimmed.replace(/^#/, '');
    if (/^[0-9a-fA-F]{6}$/.test(raw) || /^[0-9a-fA-F]{3}$/.test(raw)) {
      return normalizeHex(trimmed, displayValue);
    }
    return null;
  };

  const previewValue = normalizeIfValid(draft) || displayValue;

  const commitDraft = () => {
    const next = normalizeIfValid(draft);
    if (!next) {
      setDraft(displayValue);
      return;
    }
    onChange(next);
  };

  return (
    <label className="chat-builder-field chat-builder-field--color">
      <span className="chat-builder-field-label">{label}</span>
      <div className="chat-builder-color-row">
        <span className="chat-builder-color-swatch" style={{ background: previewValue }} aria-hidden="true" />
        <input
          className="input chat-builder-color-input"
          type="text"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false);
            commitDraft();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commitDraft();
              event.currentTarget.blur();
            }
            if (event.key === 'Escape') {
              event.preventDefault();
              setDraft(displayValue);
              event.currentTarget.blur();
            }
          }}
          disabled={disabled}
          placeholder="#3451ff"
          inputMode="text"
          autoComplete="off"
          spellCheck={false}
        />
        <div className="chat-builder-color-popover">
          <ColorPicker
            value={displayValue}
            onChange={(color) => onChange(color.toHexString())}
            disabled={disabled}
          />
        </div>
      </div>
    </label>
  );
};

function ChatBuilderEditor({
  bot,
  onBotConfigChange,
  onMenuLinkAdd,
  onMenuLinkChange,
  onMenuLinkRemove,
  themePresets,
  themePresetsLoading,
  onThemePresetSelect,
  logoUploadState,
  onLogoUpload,
  launcherIconUploadState,
  onLauncherIconUpload,
}) {
  const botId = bot?.id ?? null;
  const defaults = useMemo(() => buildDefaultConfig(bot), [bot]);
  const [config, setConfig] = useState(defaults);
  const [activeSection, setActiveSection] = useState('global');
  const [activePopover, setActivePopover] = useState(null);
  const [popoverStyle, setPopoverStyle] = useState(null);
  const popoverAnchorRef = useRef(null);
  const popoverContainerRef = useRef(null);
  const showAdvanced = true;
  const [previewView, setPreviewView] = useState('home'); // home | chat | messages
  const [importOpen, setImportOpen] = useState(false);
  const [importValue, setImportValue] = useState('');
  const [importError, setImportError] = useState(null);
  const [selectedPointId, setSelectedPointId] = useState(null);
  const [draggingPointId, setDraggingPointId] = useState(null); // display id (includes ::mirror)
  const shapeEditorRef = useRef(null);
  const previewFrameRef = useRef(null);
  const previewHeaderRef = useRef(null);
  const [previewHeaderAspect, setPreviewHeaderAspect] = useState(1);
  const logoFileInputRef = useRef(null);
  const launcherIconFileInputRef = useRef(null);
  const headerChipRef = useRef(null);
  const backgroundChipRef = useRef(null);
  const surfacesChipRef = useRef(null);
  const footerChipRef = useRef(null);
  const hydratedBotIdRef = useRef(null);

  useEffect(() => {
    if (!botId) return;
    const nextTitle = typeof bot?.botTitle === 'string' ? bot.botTitle.trim() : '';
    if (!nextTitle) return;
    setConfig((prev) => {
      const current = String(prev?.header?.title?.text || '');
      if (current === nextTitle) return prev;
      return {
        ...prev,
        header: {
          ...prev.header,
          title: {
            ...(prev.header?.title || {}),
            text: nextTitle,
          },
        },
      };
    });
  }, [botId, bot?.botTitle]);

  useEffect(() => {
    const el = previewHeaderRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      const rect = el.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const next = rect.width / rect.height;
      setPreviewHeaderAspect((prev) => (Math.abs(prev - next) < 0.01 ? prev : next));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!botId) {
      hydratedBotIdRef.current = null;
      setConfig(defaults);
      return;
    }

    if (hydratedBotIdRef.current === botId) {
      setConfig((prev) => mergeWithDefaults(defaults, prev));
      return;
    }

    hydratedBotIdRef.current = botId;
    // Prefer persisted uiConfig from the bot (server), fall back to localStorage.
    const persisted = bot?.uiConfig && typeof bot.uiConfig === 'object' ? bot.uiConfig : null;
    if (persisted) {
      setConfig(mergeWithDefaults(defaults, persisted));
      return;
    }
    const storedRaw = (() => {
      try {
        return localStorage.getItem(getStorageKey(botId));
      } catch {
        return null;
      }
    })();
    const stored = safeJsonParse(storedRaw);
    setConfig(mergeWithDefaults(defaults, stored));
  }, [botId, defaults, bot?.uiConfig]);

  useEffect(() => {
    if (!botId) return;
    setConfig((prev) => {
      const theme = prev.theme || {};
      const bg = theme.background || {};
      const surfaces = theme.surfaces || {};
      const { panel: _deprecatedPanel, ...surfacesWithoutPanel } = surfaces;
      const nextAccent = normalizeHex(bot?.color || theme.accent, theme.accent || '#3451ff');
      const nextStart = normalizeHex(bot?.gradientStart || bg.start, bg.start || nextAccent);
      const nextEnd = normalizeHex(bot?.gradientEnd || bg.end, bg.end || nextStart);
      const nextCard = normalizeHex(bot?.launcherColor || surfaces.card, surfaces.card || nextAccent);
      const nextInput = normalizeHex(bot?.inputBgColor || surfaces.input, surfaces.input || '#f4f4f5');
      const botPresetId =
        typeof bot?.themePreset === 'string' && bot.themePreset.trim()
          ? bot.themePreset.trim()
          : '';
      const nextPresetId =
        botPresetId
        || (typeof theme.presetId === 'string' && theme.presetId.trim()
          ? theme.presetId.trim()
          : 'custom');
      const nextLocked = nextPresetId !== 'custom';

      if (
        theme.accent === nextAccent
        && theme.presetId === nextPresetId
        && theme.locked === nextLocked
        && bg.start === nextStart
        && bg.end === nextEnd
        && surfaces.card === nextCard
        && surfaces.input === nextInput
      ) {
        return prev;
      }

      return {
        ...prev,
        theme: {
          ...theme,
          accent: nextAccent,
          presetId: nextPresetId,
          locked: nextLocked,
          background: {
            ...bg,
            start: nextStart,
            end: nextEnd,
          },
          surfaces: {
            ...surfacesWithoutPanel,
            card: nextCard,
            input: nextInput,
          },
        },
      };
    });
  }, [
    botId,
    bot?.color,
    bot?.gradientStart,
    bot?.gradientEnd,
    bot?.launcherColor,
    bot?.inputBgColor,
    bot?.themePreset,
  ]);

  useEffect(() => {
    setActiveSection('global');
    setPreviewView('home');
    setImportOpen(false);
    setImportError(null);
    setImportValue('');
    setSelectedPointId(null);
    setDraggingPointId(null);
  }, [botId]);

  useEffect(() => {
    if (!botId) return;
    try {
      localStorage.setItem(getStorageKey(botId), JSON.stringify(config));
    } catch {
      // ignore storage failures
    }
  }, [botId, config]);

  // Persist config into the bot draft so "Lagre endringer" stores it server-side.
  useEffect(() => {
    if (!botId || typeof onBotConfigChange !== 'function') return undefined;
    let cancelled = false;
    const timeout = setTimeout(() => {
      if (cancelled) return;
      onBotConfigChange(botId, 'uiConfig', config);
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [botId, config, onBotConfigChange]);

  const jsonValue = useMemo(() => JSON.stringify(config, null, 2), [config]);

  const setHeader = useCallback((patch) => {
    setConfig((prev) => ({
      ...prev,
      header: {
        ...prev.header,
        ...patch,
      },
    }));
  }, []);

  const setHeaderBackground = useCallback((patch) => {
    setConfig((prev) => ({
      ...prev,
      header: {
        ...prev.header,
        background: {
          ...prev.header.background,
          ...patch,
        },
      },
    }));
  }, []);

  const setHeaderLogo = useCallback((patch) => {
    setConfig((prev) => ({
      ...prev,
      header: {
        ...prev.header,
        logo: {
          ...prev.header.logo,
          ...patch,
        },
      },
    }));
  }, []);

  const setHeaderTitle = useCallback((patch) => {
    setConfig((prev) => ({
      ...prev,
      header: {
        ...prev.header,
        title: {
          ...prev.header.title,
          ...patch,
        },
      },
    }));
  }, []);

  const setHeaderSubtitle = useCallback((patch) => {
    setConfig((prev) => ({
      ...prev,
      header: {
        ...prev.header,
        subtitle: {
          ...prev.header.subtitle,
          ...patch,
        },
      },
    }));
  }, []);

  const setThemeBackground = useCallback((patch) => {
    setConfig((prev) => ({
      ...prev,
      theme: {
        ...prev.theme,
        background: {
          ...prev.theme.background,
          ...patch,
        },
      },
    }));
  }, []);

  const setThemeSurfaces = useCallback((patch) => {
    setConfig((prev) => ({
      ...prev,
      theme: {
        ...prev.theme,
        surfaces: {
          ...prev.theme.surfaces,
          ...patch,
        },
      },
    }));
  }, []);

  const setThemeFooter = useCallback((patch) => {
    setConfig((prev) => ({
      ...prev,
      theme: {
        ...prev.theme,
        footer: {
          ...(prev.theme.footer || {}),
          ...patch,
        },
      },
    }));
  }, []);

  const setThemeFooterNav = useCallback((patch) => {
    setConfig((prev) => ({
      ...prev,
      theme: {
        ...prev.theme,
        footer: {
          ...(prev.theme.footer || {}),
          nav: {
            ...((prev.theme.footer || {}).nav || {}),
            ...patch,
          },
        },
      },
    }));
  }, []);

  const setThemeFooterBg = useCallback((patch) => {
    setConfig((prev) => ({
      ...prev,
      theme: {
        ...prev.theme,
        footer: {
          ...(prev.theme.footer || {}),
          background: {
            ...((prev.theme.footer || {}).background || {}),
            ...patch,
          },
        },
      },
    }));
  }, []);

  const setHeaderShape = useCallback((patch) => {
    setConfig((prev) => ({
      ...prev,
      header: {
        ...prev.header,
        shape: {
          ...(prev.header.shape || {}),
          ...patch,
        },
      },
    }));
  }, []);

  const setHeaderShapeCustom = useCallback((updater) => {
    setConfig((prev) => {
      const prevShape = prev.header?.shape && typeof prev.header.shape === 'object' ? prev.header.shape : {};
      const prevCustom =
        prevShape.custom && typeof prevShape.custom === 'object' ? prevShape.custom : {};
      const draft = typeof updater === 'function' ? updater(prevCustom) : { ...prevCustom, ...updater };
      const normalized = normalizeHeaderShapeCustom(draft, prevCustom);

      return {
        ...prev,
        header: {
          ...prev.header,
          shape: {
            ...prevShape,
            custom: { ...prevCustom, ...draft, ...normalized },
          },
        },
      };
    });
  }, []);

  // NOTE: We intentionally do NOT auto-write computed clip-path in points-mode.
  // The clip-path string is treated as a raw override (raw mode) or derived (points mode).

  const handleCopy = useCallback(async () => {
    try {
      const ok = await copyTextToClipboard(jsonValue);
      if (ok) {
        notifySuccess('JSON kopiert.', {
          source: 'Botdesign',
          dedupeKey: `chat-builder:${botId || 'unknown'}:copy-json`,
        });
      } else {
        notifyError('Kunne ikke kopiere JSON.', {
          source: 'Botdesign',
          dedupeKey: `chat-builder:${botId || 'unknown'}:copy-json-failed`,
        });
      }
    } catch {
      notifyError('Kunne ikke kopiere JSON.', {
        source: 'Botdesign',
        dedupeKey: `chat-builder:${botId || 'unknown'}:copy-json-failed`,
      });
    }
  }, [botId, jsonValue]);

  
  const closePopover = useCallback(() => {
    setActivePopover(null);
    setPopoverStyle(null);
    popoverAnchorRef.current = null;
  }, []);

  const openPopover = useCallback((popoverId, event, anchorOverride) => {
    if (activePopover === popoverId) {
      closePopover();
      return;
    }
    if (anchorOverride?.current) {
      popoverAnchorRef.current = anchorOverride.current;
    } else if (event?.currentTarget) {
      popoverAnchorRef.current = event.currentTarget;
    }
    setActivePopover(popoverId);
  }, [activePopover, closePopover]);

  useEffect(() => {
    if (!activePopover) return undefined;

    const handleKey = (event) => {
      if (event.key === 'Escape') {
        closePopover();
      }
    };

    const handleClick = (event) => {
      const popoverEl = popoverContainerRef.current;
      const anchorEl = popoverAnchorRef.current;
      if (popoverEl && popoverEl.contains(event.target)) return;
      if (anchorEl && anchorEl.contains(event.target)) return;
      closePopover();
    };

    window.addEventListener('keydown', handleKey);
    window.addEventListener('mousedown', handleClick);
    return () => {
      window.removeEventListener('keydown', handleKey);
      window.removeEventListener('mousedown', handleClick);
    };
  }, [activePopover, closePopover]);

  const updatePopoverPosition = useCallback(() => {
    const frameEl = previewFrameRef.current;
    const anchorEl = popoverAnchorRef.current;
    const popoverEl = popoverContainerRef.current;
    if (!frameEl || !anchorEl || !popoverEl) return;
    if (!frameEl.contains(anchorEl)) {
      closePopover();
      return;
    }
    const frameRect = frameEl.getBoundingClientRect();
    const anchorRect = anchorEl.getBoundingClientRect();
    const popRect = popoverEl.getBoundingClientRect();
    if (!anchorRect.width && !anchorRect.height) {
      closePopover();
      return;
    }

    const padding = 12;
    const maxHeight = Math.max(140, Math.floor(frameRect.height - padding * 2));

    let left = anchorRect.left - frameRect.left;
    let top = anchorRect.bottom - frameRect.top + 8;
    const maxLeft = frameRect.width - popRect.width - padding;
    left = Math.min(Math.max(left, padding), Math.max(padding, maxLeft));

    const spaceBelow = frameRect.height - (anchorRect.bottom - frameRect.top) - padding;
    const spaceAbove = (anchorRect.top - frameRect.top) - padding;
    const placeBelow = spaceBelow >= popRect.height || spaceBelow >= spaceAbove;

    if (!placeBelow) {
      top = anchorRect.top - frameRect.top - popRect.height - 8;
    }

    const maxTop = frameRect.height - maxHeight - padding;
    top = Math.min(Math.max(top, padding), Math.max(padding, maxTop));
    setPopoverStyle({ top: Math.round(top), left: Math.round(left), maxHeight });
  }, [closePopover]);

  useLayoutEffect(() => {
    if (!activePopover) return undefined;
    updatePopoverPosition();
    const raf = window.requestAnimationFrame(updatePopoverPosition);
    window.addEventListener('resize', updatePopoverPosition);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', updatePopoverPosition);
    };
  }, [activePopover, previewView, updatePopoverPosition]);

  useEffect(() => {
    closePopover();
  }, [previewView, closePopover]);

  const handleReset = useCallback(() => {
    setConfig(defaults);
    setImportError(null);
    setImportValue('');
    setImportOpen(false);
    if (!botId) return;
    try {
      localStorage.removeItem(getStorageKey(botId));
    } catch {
      // ignore
    }
  }, [botId, defaults]);

  const handleApplyImport = useCallback(() => {
    const parsed = safeJsonParse(importValue);
    if (!parsed) {
      const message = 'Ugyldig JSON.';
      setImportError(message);
      notifyError(message, {
        source: 'Botdesign',
        dedupeKey: `chat-builder:${botId || 'unknown'}:import-json-invalid`,
      });
      return;
    }
    if (parsed.version !== 1) {
      const message = 'Ugyldig versjon. Forventet version: 1.';
      setImportError(message);
      notifyError(message, {
        source: 'Botdesign',
        dedupeKey: `chat-builder:${botId || 'unknown'}:import-json-version`,
      });
      return;
    }
    setConfig(mergeWithDefaults(defaults, parsed));
    setImportError(null);
    setImportOpen(false);
    notifySuccess('JSON-importen er brukt.', {
      source: 'Botdesign',
      dedupeKey: `chat-builder:${botId || 'unknown'}:import-json-applied`,
    });
  }, [botId, defaults, importValue]);

  const selectInspectorSection = useCallback((sectionId) => {
    const normalizedSectionId = (() => {
      switch (sectionId) {
        case 'design':
        case 'theme-colors':
        case 'header-shape':
        case 'font':
          return 'global';
        case 'header':
          return 'header';
        case 'home-menu':
          return 'home';
        default:
          return sectionId;
      }
    })();
    const exists = INSPECTOR_SECTIONS.some((entry) => entry.id === normalizedSectionId);
    const nextSection = exists ? normalizedSectionId : 'global';
    setActiveSection(nextSection);
  }, []);

  const computeCustomPercentFromClient = useCallback((clientX, clientY) => {
    const canvas = shapeEditorRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    return {
      x: clampNumber(((clientX - rect.left) / rect.width) * 100, 0, 100, null),
      y: clampNumber(((clientY - rect.top) / rect.height) * 100, 0, 100, null),
    };
  }, []);

  const resolveSourcePointId = useCallback(
    (displayId) => (String(displayId).endsWith('::mirror') ? String(displayId).slice(0, -8) : String(displayId)),
    [],
  );

  const applyCustomPointFromClient = useCallback(
    (displayId, clientX, clientY) => {
      const percent = computeCustomPercentFromClient(clientX, clientY);
      if (!percent) return;
      if (percent.x === null || percent.y === null) return;

      const isMirrorHandle = String(displayId).endsWith('::mirror');
      const sourceId = resolveSourcePointId(displayId);

      setHeaderShapeCustom((prevCustom) => {
        const mirror = Boolean(prevCustom?.mirror);
        const endX = mirror ? 50 : 100;
        const points = Array.isArray(prevCustom?.points) ? prevCustom.points : [];

        let nextX = clampNumber(percent.x, 0, 100, 0);
        if (mirror) {
          nextX = isMirrorHandle ? 100 - nextX : nextX;
          nextX = clampNumber(nextX, 0, 50, 0);
        } else {
          nextX = clampNumber(nextX, 0, 100, 0);
        }
        const nextY = clampNumber(percent.y, 0, 100, 100);

        const nextPoints = points.map((point) => {
          if (point.id !== sourceId) return point;
          const isEndpoint = isCustomEndpointId(point.id, mirror);
          const minX = mirror ? CUSTOM_EDGE_GUTTER : CUSTOM_EDGE_GUTTER;
          const maxX = Math.max(minX, endX - CUSTOM_EDGE_GUTTER);
          const clampedX = clampNumber(nextX, minX, maxX, point.x);
          return { ...point, x: isEndpoint ? point.x : clampedX, y: nextY };
        });

        return { ...prevCustom, points: nextPoints };
      });
    },
    [computeCustomPercentFromClient, resolveSourcePointId, setHeaderShapeCustom],
  );

  const handleCustomPointPointerDown = useCallback(
    (displayId, event) => {
      event.preventDefault();
      event.stopPropagation();
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // ignore
      }
      setDraggingPointId(displayId);
      setSelectedPointId(resolveSourcePointId(displayId));
      applyCustomPointFromClient(displayId, event.clientX, event.clientY);
    },
    [applyCustomPointFromClient, resolveSourcePointId],
  );

  const handleCustomPointPointerMove = useCallback(
    (displayId, event) => {
      if (draggingPointId !== displayId) return;
      applyCustomPointFromClient(displayId, event.clientX, event.clientY);
    },
    [applyCustomPointFromClient, draggingPointId],
  );

  const handleCustomPointPointerUp = useCallback(
    (displayId, event) => {
      if (draggingPointId !== displayId) return;
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        // ignore
      }
      setDraggingPointId(null);
    },
    [draggingPointId],
  );

  const handleCustomCanvasClick = useCallback(
    (event) => {
      const percent = computeCustomPercentFromClient(event.clientX, event.clientY);
      if (!percent) return;
      if (percent.x === null || percent.y === null) return;
      const newId = createCustomPointId();
      setSelectedPointId(newId);

      setHeaderShapeCustom((prevCustom) => {
        const mirror = Boolean(prevCustom?.mirror);
        const endX = mirror ? 50 : 100;
        const points = Array.isArray(prevCustom?.points) ? prevCustom.points : [];

        let x = clampNumber(percent.x, 0, 100, endX / 2);
        if (mirror && x > 50) x = 100 - x;
        const minX = CUSTOM_EDGE_GUTTER;
        const maxX = Math.max(minX, endX - CUSTOM_EDGE_GUTTER);
        x = clampNumber(x, minX, maxX, endX / 2);
        const y = clampNumber(percent.y, 0, 100, 100);

        return {
          ...prevCustom,
          points: [...points, { id: newId, x, y, radius: 0 }],
        };
      });
    },
    [computeCustomPercentFromClient, setHeaderShapeCustom],
  );

  const handleCustomAddPoint = useCallback(() => {
    const newId = createCustomPointId();
    setSelectedPointId(newId);
    setHeaderShapeCustom((prevCustom) => {
      const mirror = Boolean(prevCustom?.mirror);
      const endX = mirror ? 50 : 100;
      const points = Array.isArray(prevCustom?.points) ? prevCustom.points : [];
      const preferredX = mirror ? 25 : 50;
      const EPS = 0.75;

      const minX = CUSTOM_EDGE_GUTTER;
      const maxX = Math.max(minX, endX - CUSTOM_EDGE_GUTTER);
      let x = clampNumber(preferredX, minX, maxX, preferredX);
      const occupied = (xValue) => points.some((p) => Math.abs(Number(p.x) - xValue) < EPS);
      while (occupied(x) && x < maxX - 1) x += 2;
      while (occupied(x) && x > minX + 1) x -= 2;

      return {
        ...prevCustom,
        points: [...points, { id: newId, x: clampNumber(x, minX, maxX, preferredX), y: 100, radius: 0 }],
      };
    });
  }, [setHeaderShapeCustom]);

  const handleCustomDeleteSelectedPoint = useCallback(() => {
    if (!selectedPointId) return;
    setDraggingPointId(null);
    setHeaderShapeCustom((prevCustom) => {
      const mirror = Boolean(prevCustom?.mirror);
      const points = Array.isArray(prevCustom?.points) ? prevCustom.points : [];
      const target = points.find((p) => p.id === selectedPointId);
      if (!target) return prevCustom;
      if (isCustomEndpointId(target.id, mirror)) return prevCustom;
      return { ...prevCustom, points: points.filter((p) => p.id !== selectedPointId) };
    });
    setSelectedPointId(null);
  }, [selectedPointId, setHeaderShapeCustom]);

  const handleToggleCustomMirror = useCallback(
    (nextMirror) => {
      setSelectedPointId(null);
      setDraggingPointId(null);
      setHeaderShapeCustom((prevCustom) => {
        const prevMirror = Boolean(prevCustom?.mirror);
        if (prevMirror === nextMirror) return prevCustom;
        const points = Array.isArray(prevCustom?.points) ? prevCustom.points : [];

        if (nextMirror) {
          const leftPoints = [];
          const EPS = 0.25;
          const hasX = (x) => leftPoints.some((p) => Math.abs(p.x - x) < EPS);

          points.forEach((p) => {
            const safeX = clampNumber(p.x, 0, 100, 0);
            if (safeX <= 50) {
              leftPoints.push({ ...p, x: clampNumber(safeX, 0, 50, 0) });
            }
          });

          points.forEach((p) => {
            const safeX = clampNumber(p.x, 0, 100, 0);
            if (safeX <= 50) return;
            const mappedX = clampNumber(100 - safeX, 0, 50, 0);
            if (hasX(mappedX)) return;
            leftPoints.push({ ...p, id: createCustomPointId(), x: mappedX });
          });

          return { ...prevCustom, mirror: true, points: leftPoints };
        }

        const left = points.map((p) => ({ ...p, x: clampNumber(p.x, 0, 50, 0) }));
        const EPS = 0.25;
        const expanded = [...left];
        left.forEach((p) => {
          if (Math.abs(p.x - 50) < EPS) return;
          expanded.push({
            ...p,
            id: Math.abs(p.x) < EPS ? 'p-right' : createCustomPointId(),
            x: 100 - p.x,
          });
        });

        return { ...prevCustom, mirror: false, points: expanded };
      });
    },
    [setHeaderShapeCustom],
  );

  const handleSetCustomMode = useCallback(
    (nextMode) => {
      if (nextMode === 'raw') {
        const computed = buildCustomClipPath(config.header.shape.custom, previewHeaderAspect);
        if (computed && !String(config.header.shape.clipPath || '').trim()) {
          setHeaderShape({ clipPath: computed });
        }
      }
      setHeaderShapeCustom({ mode: nextMode });
    },
    [config.header.shape.clipPath, config.header.shape.custom, previewHeaderAspect, setHeaderShape, setHeaderShapeCustom],
  );

  const headerSharedBgStyle = useMemo(
    () => resolveHeaderBackgroundStyle(config.header.background),
    [config.header.background],
  );
  const headerShapeStyle = useMemo(() => {
    const shape = config.header.shape;
    if (shape?.type === 'custom') {
      const customMode = shape.custom?.mode === 'raw' ? 'raw' : 'points';
      if (customMode !== 'raw') {
        const clipPath = buildCustomClipPath(shape.custom, previewHeaderAspect);
        return clipPath ? { clipPath } : {};
      }
    }
    return resolveHeaderShapeStyle(shape);
  }, [config.header.shape, previewHeaderAspect]);
  const homeBgStyle = useMemo(
    () => resolveThemeBackgroundStyle(config.theme.background),
    [config.theme.background],
  );
  const homeHasFloatingLines = String(config.theme.background?.effect || '') === 'floating-lines';
  const preferredTextColor = config.theme.typography?.textColor || null;
  const homeTextColor = useMemo(
    () => resolvePreferredTextColor(config.theme.background.start, preferredTextColor),
    [config.theme.background.start, preferredTextColor],
  );
  const currentPresetId = typeof config.theme?.presetId === 'string' && config.theme.presetId.trim()
    ? config.theme.presetId.trim()
    : 'custom';
  const isThemeLocked = currentPresetId !== 'custom';

  const applyThemeColorPatch = useCallback((field, value) => {
    if (!isThemeLocked && bot?.id && typeof onBotConfigChange === 'function') {
      onBotConfigChange(bot.id, field, value);
    }
  }, [isThemeLocked, bot?.id, onBotConfigChange]);

  const headerSolid = config.header.background.type === 'solid'
    ? config.header.background.color
    : config.header.background.gradientStart;
  const headerSharedTextColor = useMemo(
    () => resolvePreferredTextColor(headerSolid, preferredTextColor),
    [headerSolid, preferredTextColor],
  );

  const normalizedPreviewView = previewView === 'chat' || previewView === 'messages' ? previewView : 'home';
  const isChatPreview = normalizedPreviewView === 'chat';
  const usesHomeVariant = normalizedPreviewView !== 'chat';

  const homeHeaderMode = config.header.home?.mode === 'compact' ? 'compact' : 'hero';
  const homeHeaderBackground = config.header.home?.background === 'transparent' ? 'transparent' : 'colored';

  const previewHeaderMode = isChatPreview ? 'compact' : homeHeaderMode;
  const previewHeaderBgStyle =
    usesHomeVariant && previewHeaderMode === 'hero' && homeHeaderBackground === 'transparent'
      ? { backgroundColor: 'transparent' }
      : headerSharedBgStyle;
  const previewHeaderTextColor =
    usesHomeVariant && previewHeaderMode === 'hero' && homeHeaderBackground === 'transparent'
      ? homeTextColor
      : headerSharedTextColor;
  const headerBorderEnabled =
    homeHeaderBackground !== 'transparent'
    && config.header.border.enabled
    && (config.header.shape.type === 'straight' || config.header.shape.type === 'rounded');

  // NOTE: the shared components resolve logo URL internally (botLogoUrl + uiConfig).

  const previewMenuLinks = useMemo(() => {
    const rawLinks = Array.isArray(bot?.menuLinks) ? bot.menuLinks : [];
    const normalized = rawLinks
      .filter((link) => link && typeof link === 'object')
      .map((link, index) => ({
        key: String(link.clientKey || link.id || index),
        label: String(link.label || '').trim(),
        description: String(link.description || '').trim(),
        icon: String(link.icon || '').trim(),
      }))
      .filter((link) => link.label || link.description)
      .slice(0, 6);
    return normalized;
  }, [bot?.menuLinks]);

  const homeIntroTitle = (bot?.menuIntroTitle || '').trim() || 'Hva kan jeg hjelpe deg med?';
  const homeIntroSubtitle = (bot?.menuIntroSubtitle || '').trim() || 'Velg et tema for å komme i gang.';

  const composerInputBg = config.theme.surfaces.input;
  const composerIsLight = useMemo(() => getLuminance(composerInputBg) > 0.5, [composerInputBg]);
  const composerText = useMemo(
    () => resolvePreferredTextColor(composerInputBg, preferredTextColor),
    [composerInputBg, preferredTextColor],
  );
  const startButtonBg = config.theme.surfaces.card;
  const startButtonText = useMemo(
    () => resolvePreferredTextColor(startButtonBg, preferredTextColor),
    [startButtonBg, preferredTextColor],
  );
  const footerVariant = config.theme.footer?.style === 'dock' ? 'dock' : 'minimal';
  const renderInspectorPanel = () => {
    const renderHeaderShapeControls = () => {
      const isCustom = config.header.shape.type === 'custom';
      const custom = config.header.shape.custom && typeof config.header.shape.custom === 'object'
        ? config.header.shape.custom
        : {};
      const customMode = custom.mode === 'raw' ? 'raw' : 'points';
      const mirror = Boolean(custom.mirror);
      const endX = mirror ? 50 : 100;
      const rawClipPath = typeof config.header.shape.clipPath === 'string' ? config.header.shape.clipPath.trim() : '';
      const computedClipPath = buildCustomClipPath(custom, previewHeaderAspect);
      const previewClipPath = customMode === 'raw' ? rawClipPath : computedClipPath || rawClipPath;

      const customPoints = Array.isArray(custom.points) ? custom.points : [];
      const pointById = new Map(customPoints.map((p) => [p.id, p]));
      const selectedPoint = selectedPointId ? pointById.get(selectedPointId) : null;
      const selectedIsEndpoint = (() => {
        if (!selectedPoint) return false;
        return isCustomEndpointId(selectedPoint.id, mirror);
      })();

      const displayPoints = buildCustomFullPoints(custom).map((p) => ({
        ...p,
        sourceId: String(p.id).endsWith('::mirror') ? String(p.id).slice(0, -8) : String(p.id),
        isMirror: String(p.id).endsWith('::mirror'),
      }));

      return (
        <div className="chat-builder-fields">
          <label className="chat-builder-field">
            <span className="chat-builder-field-label">Form</span>
            <select
              className="select"
              value={config.header.shape.type}
              onChange={(event) => setHeaderShape({ type: event.target.value })}
            >
              <option value="straight">Rett</option>
              <option value="rounded">Rundet bunn</option>
              <option value="angled">Skrå bunn</option>
              <option value="wave">Bølge</option>
              <option value="custom">Custom</option>
            </select>
          </label>

          {config.header.shape.type === 'rounded' && (
            <label className="chat-builder-field">
              <span className="chat-builder-field-label">Radius (bunn)</span>
              <input
                className="input"
                type="number"
                min={0}
                max={48}
                value={config.header.shape.radius}
                onChange={(event) => setHeaderShape({ radius: clampNumber(event.target.value, 0, 48, 18) })}
              />
            </label>
          )}

          {config.header.shape.type === 'angled' && (
            <div className="chat-builder-field chat-builder-field--group">
              <label className="chat-builder-field">
                <span className="chat-builder-field-label">Retning</span>
                <select
                  className="select"
                  value={config.header.shape.angleDirection}
                  onChange={(event) => setHeaderShape({ angleDirection: event.target.value })}
                >
                  <option value="right">Skrå mot høyre</option>
                  <option value="left">Skrå mot venstre</option>
                </select>
              </label>
              <label className="chat-builder-field">
                <span className="chat-builder-field-label">Kutt (%)</span>
                <input
                  className="input"
                  type="number"
                  min={0}
                  max={48}
                  value={config.header.shape.angleCut}
                  onChange={(event) => setHeaderShape({ angleCut: clampNumber(event.target.value, 0, 48, 14) })}
                />
                <span className="input-hint">Prosent av header-høyden.</span>
              </label>
            </div>
          )}

          {config.header.shape.type === 'wave' && (
            <div className="chat-builder-field chat-builder-field--group">
              <label className="chat-builder-field">
                <span className="chat-builder-field-label">Amplitude (%)</span>
                <input
                  className="input"
                  type="number"
                  min={0}
                  max={35}
                  value={config.header.shape.waveAmplitude}
                  onChange={(event) =>
                    setHeaderShape({ waveAmplitude: clampNumber(event.target.value, 0, 35, 14) })}
                />
              </label>
              <label className="chat-builder-field">
                <span className="chat-builder-field-label">Bølger</span>
                <input
                  className="input"
                  type="number"
                  min={1}
                  max={6}
                  value={config.header.shape.waveFrequency}
                  onChange={(event) =>
                    setHeaderShape({ waveFrequency: clampNumber(event.target.value, 1, 6, 2) })}
                />
              </label>
            </div>
          )}

          {isCustom && (
            <>
              <div className="chat-builder-field chat-builder-field--segmented">
                <button
                  type="button"
                  className={`chat-builder-segment ${customMode === 'points' ? 'is-active' : ''}`}
                  onClick={() => handleSetCustomMode('points')}
                >
                  Punkter
                </button>
                <button
                  type="button"
                  className={`chat-builder-segment ${customMode === 'raw' ? 'is-active' : ''}`}
                  onClick={() => handleSetCustomMode('raw')}
                >
                  Raw clip-path
                </button>
              </div>

              {customMode === 'points' && (
                <div className="chat-builder-shape-editor">
                  <label className="chat-builder-field chat-builder-field--toggle">
                    <span className="chat-builder-field-label">Mirror</span>
                    <input
                      type="checkbox"
                      checked={mirror}
                      onChange={(event) => handleToggleCustomMirror(event.target.checked)}
                    />
                  </label>

                  <label className="chat-builder-field">
                    <span className="chat-builder-field-label">Runding (kvalitet)</span>
                    <input
                      className="input"
                      type="number"
                      min={2}
                      max={16}
                      value={custom.roundingSegments ?? 6}
                      onChange={(event) =>
                        setHeaderShapeCustom({
                          roundingSegments: clampNumber(event.target.value, 2, 16, 6),
                        })}
                    />
                    <span className="input-hint">Flere segmenter gir glattere hjørner (tyngre polygon).</span>
                  </label>

                  <div className="chat-builder-hint">
                    Klikk i forhåndsvisningen for å legge til et punkt. Dra et punkt for å flytte det. Velg et punkt for å runde hjørnet.
                  </div>

                  <div
                    className="chat-builder-shape-canvas"
                    ref={shapeEditorRef}
                    onClick={handleCustomCanvasClick}
                    role="presentation"
                  >
                    <div className="chat-builder-shape-preview" style={{ ...headerSharedBgStyle, clipPath: previewClipPath }} />
                    {mirror && <div className="chat-builder-shape-midline" aria-hidden="true" />}

                    {displayPoints.map((point) => {
                      const sourcePoint = pointById.get(point.sourceId);
                      const isEndpoint = sourcePoint ? isCustomEndpointId(sourcePoint.id, mirror) : false;
                      const isSelected = point.sourceId === selectedPointId;
                      const isDragging = point.id === draggingPointId;

                      return (
                        <button
                          key={point.id}
                          type="button"
                          className={[
                            'chat-builder-point-handle',
                            point.isMirror ? 'is-mirror' : '',
                            isSelected ? 'is-selected' : '',
                            isEndpoint ? 'is-locked' : '',
                            isDragging ? 'is-dragging' : '',
                          ].filter(Boolean).join(' ')}
                          style={{ left: `${point.x}%`, top: `${point.y}%` }}
                          onPointerDown={(event) => handleCustomPointPointerDown(point.id, event)}
                          onPointerMove={(event) => handleCustomPointPointerMove(point.id, event)}
                          onPointerUp={(event) => handleCustomPointPointerUp(point.id, event)}
                          onPointerCancel={(event) => handleCustomPointPointerUp(point.id, event)}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            setSelectedPointId(point.sourceId);
                          }}
                          aria-label={point.isMirror ? 'Speilet punkt' : 'Punkt'}
                        />
                      );
                    })}
                  </div>

                  <div className="chat-builder-shape-toolbar">
                    <button type="button" className="button" onClick={handleCustomAddPoint}>
                      Legg til punkt
                    </button>
                    <button
                      type="button"
                      className="button button-danger"
                      onClick={handleCustomDeleteSelectedPoint}
                      disabled={!selectedPoint || selectedIsEndpoint}
                      title={selectedIsEndpoint ? 'Endepunkter kan ikke slettes.' : 'Slett valgt punkt'}
                    >
                      Slett punkt
                    </button>
                    <button
                      type="button"
                      className="button"
                      onClick={() => {
                        setSelectedPointId(null);
                        setDraggingPointId(null);
                        setHeaderShapeCustom((prevCustom) => {
                          const mirrorEnabled = Boolean(prevCustom?.mirror);
                          return {
                            ...prevCustom,
                            points: mirrorEnabled
                              ? [
                                  { id: 'p-left', x: 0, y: 100, radius: 0 },
                                  { id: 'p-center', x: 50, y: 100, radius: 0 },
                                ]
                              : [
                                  { id: 'p-left', x: 0, y: 100, radius: 0 },
                                  { id: 'p-right', x: 100, y: 100, radius: 0 },
                                ],
                          };
                        });
                      }}
                    >
                      Reset form
                    </button>
                  </div>

                  {selectedPoint ? (
                    <div className="chat-builder-point-editor">
                      <h4>Valgt punkt</h4>
                      <div className="chat-builder-fields">
                        <div className="chat-builder-field chat-builder-field--group">
                          <label className="chat-builder-field">
                            <span className="chat-builder-field-label">X (%)</span>
                            <input
                              className="input"
                              type="number"
                              min={CUSTOM_EDGE_GUTTER}
                              max={Math.max(CUSTOM_EDGE_GUTTER, endX - CUSTOM_EDGE_GUTTER)}
                              step={0.5}
                              value={selectedPoint.x}
                              disabled={selectedIsEndpoint}
                              onChange={(event) => {
                                const nextX = clampNumber(
                                  event.target.value,
                                  CUSTOM_EDGE_GUTTER,
                                  Math.max(CUSTOM_EDGE_GUTTER, endX - CUSTOM_EDGE_GUTTER),
                                  selectedPoint.x,
                                );
                                setHeaderShapeCustom((prevCustom) => ({
                                  ...prevCustom,
                                  points: Array.isArray(prevCustom?.points)
                                    ? prevCustom.points.map((p) => (p.id === selectedPoint.id ? { ...p, x: nextX } : p))
                                    : prevCustom.points,
                                }));
                              }}
                            />
                          </label>
                          <label className="chat-builder-field">
                            <span className="chat-builder-field-label">Y (%)</span>
                            <input
                              className="input"
                              type="number"
                              min={0}
                              max={100}
                              step={0.5}
                              value={selectedPoint.y}
                              onChange={(event) => {
                                const nextY = clampNumber(event.target.value, 0, 100, selectedPoint.y);
                                setHeaderShapeCustom((prevCustom) => ({
                                  ...prevCustom,
                                  points: Array.isArray(prevCustom?.points)
                                    ? prevCustom.points.map((p) => (p.id === selectedPoint.id ? { ...p, y: nextY } : p))
                                    : prevCustom.points,
                                }));
                              }}
                            />
                          </label>
                        </div>

                        <label className="chat-builder-field">
                          <span className="chat-builder-field-label">Runding</span>
                          <div className="chat-builder-range-row">
                            <input
                              className="range"
                              type="range"
                              min={0}
                              max={CUSTOM_MAX_POINT_RADIUS}
                              step={0.25}
                              value={selectedPoint.radius ?? 0}
                              onChange={(event) => {
                                const nextRadius = clampNumber(event.target.value, 0, CUSTOM_MAX_POINT_RADIUS, 0);
                                setHeaderShapeCustom((prevCustom) => ({
                                  ...prevCustom,
                                  points: Array.isArray(prevCustom?.points)
                                    ? prevCustom.points.map((p) =>
                                        p.id === selectedPoint.id ? { ...p, radius: nextRadius } : p,
                                      )
                                    : prevCustom.points,
                                }));
                              }}
                            />
                            <input
                              className="input"
                              type="number"
                              min={0}
                              max={CUSTOM_MAX_POINT_RADIUS}
                              step={0.25}
                              value={selectedPoint.radius ?? 0}
                              onChange={(event) => {
                                const nextRadius = clampNumber(event.target.value, 0, CUSTOM_MAX_POINT_RADIUS, 0);
                                setHeaderShapeCustom((prevCustom) => ({
                                  ...prevCustom,
                                  points: Array.isArray(prevCustom?.points)
                                    ? prevCustom.points.map((p) =>
                                        p.id === selectedPoint.id ? { ...p, radius: nextRadius } : p,
                                      )
                                    : prevCustom.points,
                                }));
                              }}
                            />
                          </div>
                          <span className="input-hint">0 = spiss, høyere = rundere hjørne.</span>
                        </label>
                      </div>
                    </div>
                  ) : (
                    <p className="chat-builder-muted">Klikk på et punkt for å redigere posisjon og runding.</p>
                  )}

                  <div className="chat-builder-hint">
                    <code>{computedClipPath || rawClipPath || 'clip-path: (tom)'}</code>
                  </div>
                </div>
              )}

              {customMode === 'raw' && (
                <label className="chat-builder-field">
                  <span className="chat-builder-field-label">clip-path</span>
                  <textarea
                    className="textarea chat-builder-clip-textarea"
                    rows={4}
                    value={config.header.shape.clipPath}
                    onChange={(event) => setHeaderShape({ clipPath: event.target.value })}
                    placeholder="polygon(0% 0%, 100% 0%, 100% 80%, 0% 100%)"
                  />
                  <span className="input-hint">
                    Eksempler: <code>polygon(...)</code>, <code>circle(...)</code>, <code>ellipse(...)</code>,{' '}
                    <code>path(...)</code>.
                  </span>
                </label>
              )}
            </>
          )}
        </div>
      );
    };

    switch (activeSection) {
      case 'global': {
        const basePresets = Array.isArray(themePresets) ? themePresets : [];
        const presets = basePresets.some((preset) => preset?.id === 'custom')
          ? basePresets
          : [{ id: 'custom', label: 'Tilpasset', colors: null }, ...basePresets];
        const currentPresetId = typeof config.theme?.presetId === 'string' && config.theme.presetId.trim()
          ? config.theme.presetId.trim()
          : 'custom';
        const isLockedTheme = currentPresetId !== 'custom';

        const handleThemePresetApply = (presetId) => {
          if (bot?.id && typeof onThemePresetSelect === 'function') {
            onThemePresetSelect(bot.id, presetId);
          } else if (bot?.id && typeof onBotConfigChange === 'function') {
            onBotConfigChange(bot.id, 'themePreset', presetId);
          }

          const preset = presets.find((entry) => entry.id === presetId);
          setConfig((prev) => ({
            ...prev,
            header: presetId !== 'custom' && preset?.colors
              ? {
                  ...prev.header,
                  background: {
                    ...prev.header.background,
                    type: 'solid',
                    color: normalizeHex(preset.colors.color, prev.header.background?.color || '#0f172a'),
                  },
                }
              : prev.header,
            theme: {
              ...prev.theme,
              presetId,
              locked: presetId !== 'custom',
              accent: preset?.colors ? normalizeHex(preset.colors.color, prev.theme.accent) : prev.theme.accent,
              background: {
                ...prev.theme.background,
                type: 'gradient',
                effect: preset?.backgroundPresetSlug === 'floating-lines' || preset?.id === 'floating-lines'
                  ? 'floating-lines'
                  : 'none',
                start: preset?.colors
                  ? normalizeHex(preset.colors.gradientStart, prev.theme.background.start)
                  : prev.theme.background.start,
                end: preset?.colors
                  ? normalizeHex(preset.colors.gradientEnd, prev.theme.background.end)
                  : prev.theme.background.end,
              },
              surfaces: {
                card: preset?.colors
                  ? normalizeHex(preset.colors.launcherColor, prev.theme.surfaces.card)
                  : prev.theme.surfaces.card,
                input: preset?.colors
                  ? normalizeHex(preset.colors.inputBgColor, prev.theme.surfaces.input)
                  : prev.theme.surfaces.input,
              },
            },
          }));
        };

        return (
          <div className="chat-builder-inspector-section">
            <h3>Globalt</h3>
            <div className="chat-builder-fields">
              <details className="chat-builder-collapse" open>
                <summary>Hurtigoppsett</summary>
                <div className="chat-builder-collapse-body">
                  <p className="chat-builder-muted">
                    Velg et tema. Når du velger et preset-tema blir fargevalgene låst (til du velger <code>Tilpasset</code>).
                  </p>

                  <div className="chat-builder-theme-grid" aria-busy={themePresetsLoading ? 'true' : 'false'}>
                    {presets.map((preset) => {
                      const isSelected = preset.id === currentPresetId;
                      const shouldMute = isLockedTheme && !isSelected;
                      const swatches = preset.colors
                        ? [preset.colors.gradientStart, preset.colors.gradientEnd, preset.colors.color, preset.colors.launcherColor]
                            .filter(Boolean)
                            .slice(0, 4)
                        : [];
                      return (
                        <button
                          key={preset.id}
                          type="button"
                          className={[
                            'chat-builder-theme-card',
                            isSelected ? 'is-selected' : '',
                            shouldMute ? 'is-muted' : '',
                          ].filter(Boolean).join(' ')}
                          onClick={() => handleThemePresetApply(preset.id)}
                          disabled={themePresetsLoading}
                        >
                          <div className="chat-builder-theme-swatches" aria-hidden="true">
                            {swatches.length ? (
                              swatches.map((color, index) => (
                                <span key={`${preset.id}-${index}`} className="chat-builder-theme-swatch" style={{ background: color }} />
                              ))
                            ) : (
                              <span className="chat-builder-theme-custom">🎨</span>
                            )}
                          </div>
                          <div className="chat-builder-theme-name">{preset.label || preset.id}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </details>


              <details className="chat-builder-collapse">
                <summary>Typografi</summary>
                <div className="chat-builder-collapse-body">
                  <label className="chat-builder-field">
                    <span className="chat-builder-field-label">Font</span>
                    <select
                      className="select"
                      value={(() => {
                        const typography =
                          config.theme.typography && typeof config.theme.typography === 'object' ? config.theme.typography : {};
                        const fontFamily = normalizeFontFamily(typography.fontFamily, DEFAULT_FONT_FAMILY);
                        const preset = FONT_PRESETS.find((p) => p.value === fontFamily);
                        return preset?.id || 'default';
                      })()}
                      onChange={(event) => {
                        const nextId = event.target.value;
                        const preset = FONT_PRESETS.find((p) => p.id === nextId) || FONT_PRESETS[0];
                        setConfig((prev) => ({
                          ...prev,
                          theme: {
                            ...prev.theme,
                            typography: {
                              ...(prev.theme.typography || {}),
                              fontFamily: preset.value,
                            },
                          },
                        }));
                      }}
                    >
                      {FONT_PRESETS.map((preset) => (
                        <option key={preset.id} value={preset.id}>
                          {preset.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </details>

              <details className="chat-builder-collapse">
                <summary>Chatikon</summary>
                <div className="chat-builder-collapse-body">
                  <div className="chat-builder-logo">
                    <div className="chat-builder-logo-preview chat-builder-logo-preview--launcher">
                      {bot?.launcherIconPreviewUrl ? (
                        <img src={bot.launcherIconPreviewUrl} alt="Chatikon" />
                      ) : (
                        <div className="chat-builder-logo-placeholder" aria-hidden="true">
                          KI
                        </div>
                      )}
                    </div>
                    <div className="chat-builder-logo-controls">
                      <input
                        ref={launcherIconFileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/gif,image/webp"
                        style={{ display: 'none' }}
                        onChange={(event) => {
                          const file = event.target.files?.[0] || null;
                          event.target.value = '';
                          if (!file) return;
                          if (typeof onLauncherIconUpload === 'function' && bot) {
                            onLauncherIconUpload(bot, file);
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="button"
                        onClick={() => launcherIconFileInputRef.current?.click()}
                        disabled={!bot || Boolean(launcherIconUploadState?.[bot?.id]?.uploading)}
                      >
                        {launcherIconUploadState?.[bot?.id]?.uploading ? 'Laster opp…' : 'Last opp ikon'}
                      </button>
                      <div className="chat-builder-hint">
                        PNG, JPG, GIF eller WebP. Store PNG/JPG/WebP skaleres ned automatisk.
                      </div>
                      {launcherIconUploadState?.[bot?.id]?.error && (
                        <div className="chat-builder-error">{launcherIconUploadState?.[bot?.id]?.error}</div>
                      )}
                    </div>
                  </div>

                  <label className="chat-builder-field">
                    <span className="chat-builder-field-label">Ikon-URL</span>
                    <input
                      className="input"
                      type="text"
                      value={bot?.launcherIconUrl || ''}
                      onChange={(event) => {
                        if (bot?.id) {
                          onBotConfigChange?.(bot.id, 'launcherIconUrl', event.target.value);
                        }
                      }}
                      placeholder="https://..."
                    />
                  </label>

                  <div className="chat-builder-field chat-builder-field--group">
                    <label className="chat-builder-field">
                      <span className="chat-builder-field-label">Størrelse</span>
                      <input
                        className="input"
                        type="range"
                        min={44}
                        max={96}
                        value={bot?.launcherIconSize || 64}
                        onChange={(event) => {
                          if (bot?.id) {
                            onBotConfigChange?.(bot.id, 'launcherIconSize', event.target.value);
                          }
                        }}
                      />
                    </label>
                    <label className="chat-builder-field">
                      <span className="chat-builder-field-label">px</span>
                      <input
                        className="input"
                        type="number"
                        min={44}
                        max={96}
                        value={bot?.launcherIconSize || 64}
                        onChange={(event) => {
                          if (bot?.id) {
                            onBotConfigChange?.(bot.id, 'launcherIconSize', event.target.value);
                          }
                        }}
                      />
                    </label>
                  </div>

                  <button
                    type="button"
                    className="button button-secondary"
                    onClick={() => {
                      if (!bot?.id) return;
                      onBotConfigChange?.(bot.id, 'launcherIconUrl', '');
                      onBotConfigChange?.(bot.id, 'launcherIconSize', 64);
                    }}
                  >
                    Tilbakestill chatikon
                  </button>
                </div>
              </details>
            </div>
          </div>
        );
      }
      case 'header': {
        const homeHeaderModeValue =
          config.header.home.mode === 'compact'
            ? 'compact'
            : (config.header.home.background === 'transparent' ? 'transparent' : 'colored');
        const headerPreviewMode = previewView === 'chat' ? 'chat' : 'home';

        return (
          <div className="chat-builder-inspector-section">
            <h3>Header</h3>
            <div className="chat-builder-fields">
              <details className="chat-builder-collapse" open>
                <summary>Header-stil</summary>
                <div className="chat-builder-collapse-body">
                  <div className="chat-builder-hint">
                    Header-stilen deles mellom hjem og chat. Farger redigeres i forhåndsvisningen.
                  </div>
                  <label className="chat-builder-field">
                    <span className="chat-builder-field-label">Template</span>
                    <select
                      className="select"
                      value={config.header.template}
                      onChange={(event) => setHeader({ template: event.target.value })}
                    >
                      <option value="classic">Classic</option>
                      <option value="brandbar">Brandbar</option>
                      <option value="minimal">Minimal</option>
                    </select>
                  </label>

                  <label className="chat-builder-field chat-builder-field--toggle">
                    <span className="chat-builder-field-label">Bunnlinje</span>
                    <input
                      type="checkbox"
                      checked={config.header.border.enabled}
                      onChange={(event) =>
                        setHeader({ border: { ...config.header.border, enabled: event.target.checked } })}
                    />
                  </label>

                  {config.header.border.enabled && (
                    <label className="chat-builder-field">
                      <span className="chat-builder-field-label">Bunnlinje farge</span>
                      <input
                        className="input"
                        type="text"
                        value={config.header.border.color}
                        onChange={(event) => setHeader({ border: { ...config.header.border, color: event.target.value } })}
                      />
                    </label>
                  )}
                </div>
              </details>

              <details className="chat-builder-collapse">
                <summary>Header-form</summary>
                <div className="chat-builder-collapse-body">
                  {renderHeaderShapeControls()}
                </div>
              </details>

              <div className="chat-builder-field chat-builder-field--segmented">
                <button
                  type="button"
                  className={`chat-builder-segment ${headerPreviewMode === 'home' ? 'is-active' : ''}`}
                  onClick={() => setPreviewView('home')}
                  aria-pressed={headerPreviewMode === 'home'}
                >
                  Hjem-visning
                </button>
                <button
                  type="button"
                  className={`chat-builder-segment ${headerPreviewMode === 'chat' ? 'is-active' : ''}`}
                  onClick={() => setPreviewView('chat')}
                  aria-pressed={headerPreviewMode === 'chat'}
                >
                  Chat-visning
                </button>
              </div>

              {headerPreviewMode === 'home' ? (
                <details className="chat-builder-collapse" open>
                  <summary>Header-innhold (hjem)</summary>
                  <div className="chat-builder-collapse-body">
                    <label className="chat-builder-field">
                      <span className="chat-builder-field-label">Header (hjem)</span>
                      <select
                        className="select"
                        value={homeHeaderModeValue}
                        onChange={(event) => {
                          const value = event.target.value;
                          if (value === 'compact') {
                            setHeader({ home: { ...config.header.home, mode: 'compact', background: 'colored' } });
                            return;
                          }
                          setHeader({
                            home: {
                              ...config.header.home,
                              mode: 'hero',
                              background: value === 'transparent' ? 'transparent' : 'colored',
                            },
                          });
                        }}
                      >
                        <option value="transparent">Transparent (stor)</option>
                        <option value="colored">Farget (stor)</option>
                        <option value="compact">Kompakt</option>
                      </select>
                    </label>

                    <div className="chat-builder-field chat-builder-field--group">
                      <label className="chat-builder-field">
                        <span className="chat-builder-field-label">Plassering (hjem)</span>
                        <select
                          className="select"
                          value={config.header.home.align}
                          onChange={(event) =>
                            setHeader({ home: { ...config.header.home, align: event.target.value === 'center' ? 'center' : 'left' } })}
                          disabled={config.header.home.mode === 'compact'}
                        >
                          <option value="left">Venstre</option>
                          <option value="center">Sentrert</option>
                        </select>
                      </label>
                      <label className="chat-builder-field">
                        <span className="chat-builder-field-label">Logo størrelse (hjem)</span>
                        <input
                          className="input"
                          type="number"
                          min={24}
                          max={160}
                          value={config.header.home.logoSize}
                          onChange={(event) =>
                            setHeader({ home: { ...config.header.home, logoSize: clampNumber(event.target.value, 24, 160, 80) } })}
                          disabled={config.header.home.mode === 'compact'}
                        />
                      </label>
                    </div>

                    <label className="chat-builder-field">
                      <span className="chat-builder-field-label">Innhold (hjem)</span>
                      <select
                        className="select"
                        value={config.header.home.content}
                        onChange={(event) =>
                          setHeader({ home: { ...config.header.home, content: event.target.value } })}
                        disabled={config.header.home.mode === 'compact'}
                      >
                        <option value="logo">Kun logo</option>
                        <option value="title">Kun tittel</option>
                        <option value="both">Logo + tittel</option>
                      </select>
                    </label>

                    <label className="chat-builder-field">
                      <span className="chat-builder-field-label">Tittel</span>
                      <input
                        className="input"
                        type="text"
                        value={config.header.title.text}
                        onChange={(event) => {
                          const value = event.target.value;
                          setHeaderTitle({ text: value });
                          if (bot?.id) {
                            onBotConfigChange?.(bot.id, 'botTitle', value);
                          }
                        }}
                        placeholder="KI Chat"
                      />
                    </label>

                    <label className="chat-builder-field chat-builder-field--toggle">
                      <span className="chat-builder-field-label">Vis undertittel</span>
                      <input
                        type="checkbox"
                        checked={config.header.subtitle.enabled}
                        onChange={(event) => setHeaderSubtitle({ enabled: event.target.checked })}
                      />
                    </label>

                    {config.header.subtitle.enabled && (
                      <label className="chat-builder-field">
                        <span className="chat-builder-field-label">Undertittel</span>
                        <input
                          className="input"
                          type="text"
                          value={config.header.subtitle.text}
                          onChange={(event) => setHeaderSubtitle({ text: event.target.value })}
                          placeholder="Spør oss om hva som helst"
                        />
                      </label>
                    )}

                    <label className="chat-builder-field">
                      <span className="chat-builder-field-label">Logo</span>
                      <select
                        className="select"
                        value={config.header.logo.mode}
                        onChange={(event) => setHeaderLogo({ mode: event.target.value })}
                      >
                        <option value="none">Ingen</option>
                        <option value="botLogo">Bot-logo</option>
                        <option value="url">URL</option>
                      </select>
                    </label>

                    {config.header.logo.mode === 'botLogo' && (
                      <div className="chat-builder-logo">
                        <div className="chat-builder-logo-preview">
                          {bot?.logoPreviewUrl ? (
                            <img src={bot.logoPreviewUrl} alt="Bot-logo" />
                          ) : (
                            <div className="chat-builder-logo-placeholder" aria-hidden="true">
                              {String(config.header.title.text || 'KI').slice(0, 2).toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="chat-builder-logo-controls">
                          <input
                            ref={logoFileInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp"
                            style={{ display: 'none' }}
                            onChange={(event) => {
                              const file = event.target.files?.[0] || null;
                              event.target.value = '';
                              if (!file) return;
                              if (typeof onLogoUpload === 'function' && bot) {
                                onLogoUpload(bot, file);
                              }
                            }}
                          />
                          <button
                            type="button"
                            className="button"
                            onClick={() => logoFileInputRef.current?.click()}
                            disabled={!bot || Boolean(logoUploadState?.[bot?.id]?.uploading)}
                          >
                            {logoUploadState?.[bot?.id]?.uploading ? 'Laster opp…' : 'Last opp logo'}
                          </button>
                          <div className="chat-builder-hint">PNG, JPG, GIF, SVG eller WebP (maks 2 MB).</div>
                          {logoUploadState?.[bot?.id]?.error && (
                            <div className="chat-builder-error">{logoUploadState?.[bot?.id]?.error}</div>
                          )}
                        </div>
                      </div>
                    )}

                    {config.header.logo.mode === 'url' && (
                      <label className="chat-builder-field">
                        <span className="chat-builder-field-label">Logo-URL</span>
                        <input
                          className="input"
                          type="text"
                          value={config.header.logo.url}
                          onChange={(event) => setHeaderLogo({ url: event.target.value })}
                          placeholder="https://…"
                        />
                      </label>
                    )}

                    {config.header.logo.mode !== 'none' && (
                      <label className="chat-builder-field">
                        <span className="chat-builder-field-label">Hjørneradius</span>
                        <input
                          className="input"
                          type="number"
                          min={0}
                          max={24}
                          value={config.header.logo.cornerRadius}
                          onChange={(event) =>
                            setHeaderLogo({ cornerRadius: clampNumber(event.target.value, 0, 24, 10) })}
                        />
                      </label>
                    )}
                  </div>
                </details>
              ) : (
                <details className="chat-builder-collapse" open>
                  <summary>Header-innhold (chat)</summary>
                  <div className="chat-builder-collapse-body">
                    <label className="chat-builder-field">
                      <span className="chat-builder-field-label">Innhold (chat)</span>
                      <select
                        className="select"
                        value={config.header.compact.content}
                        onChange={(event) =>
                          setHeader({ compact: { ...config.header.compact, content: event.target.value } })}
                      >
                        <option value="logo">Kun logo</option>
                        <option value="title">Kun tittel</option>
                        <option value="both">Logo + tittel</option>
                      </select>
                    </label>
                  </div>
                </details>
              )}
            </div>
          </div>
        );
      }
      case 'home': {
        return (
          <div className="chat-builder-inspector-section">
            <h3>Hjem</h3>
            <div className="chat-builder-fields">

              <details className="chat-builder-collapse">
                <summary>Meny-intro</summary>
                <div className="chat-builder-collapse-body">
                  <p className="chat-builder-muted">
                    Endringene lagres sammen med boten når du trykker <code>Lagre endringer</code>.
                  </p>
                  <label className="chat-builder-field">
                    <span className="chat-builder-field-label">Overskrift</span>
                    <input
                      className="input"
                      type="text"
                      value={bot?.menuIntroTitle || ''}
                      onChange={(event) => onBotConfigChange?.(bot?.id, 'menuIntroTitle', event.target.value)}
                      placeholder="Hei! Hvordan kan vi hjelpe?"
                      disabled={!bot?.id}
                    />
                  </label>

                  <label className="chat-builder-field">
                    <span className="chat-builder-field-label">Undertittel</span>
                    <input
                      className="input"
                      type="text"
                      value={bot?.menuIntroSubtitle || ''}
                      onChange={(event) => onBotConfigChange?.(bot?.id, 'menuIntroSubtitle', event.target.value)}
                      placeholder="Velg et alternativ under"
                      disabled={!bot?.id}
                    />
                  </label>
                </div>
              </details>

              <details className="chat-builder-collapse">
                <summary>Snarveier</summary>
                <div className="chat-builder-collapse-body">
                  <div className="chat-builder-hint">
                    Snarveier vises som kort i hjem-menyen (maks 6 i forhåndsvisningen).
                  </div>

                  {(bot?.menuLinks || []).length === 0 ? (
                    <p className="chat-builder-muted">Ingen snarveier enda.</p>
                  ) : (
                    <div className="chat-builder-menu-links">
                      {(bot?.menuLinks || []).map((link) => (
                        <div key={link.clientKey} className="chat-builder-menu-link-card">
                          <div className="chat-builder-field chat-builder-field--group">
                            <label className="chat-builder-field">
                              <span className="chat-builder-field-label">Tekst</span>
                              <input
                                className="input"
                                type="text"
                                value={link.label || ''}
                                onChange={(event) =>
                                  onMenuLinkChange?.(bot?.id, link.clientKey, 'label', event.target.value)}
                                placeholder="FAQ"
                                disabled={!bot?.id}
                              />
                            </label>
                            <label className="chat-builder-field">
                              <span className="chat-builder-field-label">URL</span>
                              <input
                                className="input"
                                type="url"
                                value={link.url || ''}
                                onChange={(event) =>
                                  onMenuLinkChange?.(bot?.id, link.clientKey, 'url', event.target.value)}
                                placeholder="https://…"
                                disabled={!bot?.id}
                              />
                            </label>
                          </div>

                          <div className="chat-builder-field chat-builder-field--group">
                            <label className="chat-builder-field">
                              <span className="chat-builder-field-label">Beskrivelse</span>
                              <input
                                className="input"
                                type="text"
                                value={link.description || ''}
                                onChange={(event) =>
                                  onMenuLinkChange?.(bot?.id, link.clientKey, 'description', event.target.value)}
                                placeholder="Vanlige spørsmål og svar"
                                disabled={!bot?.id}
                              />
                            </label>
                            <label className="chat-builder-field">
                              <span className="chat-builder-field-label">Ikon / emoji</span>
                              <input
                                className="input"
                                type="text"
                                value={link.icon || ''}
                                onChange={(event) =>
                                  onMenuLinkChange?.(bot?.id, link.clientKey, 'icon', event.target.value)}
                                placeholder="📚"
                                disabled={!bot?.id}
                              />
                            </label>
                          </div>

                          <button
                            type="button"
                            className="button button-danger"
                            onClick={() => onMenuLinkRemove?.(bot?.id, link.clientKey)}
                            disabled={!bot?.id}
                          >
                            Fjern snarvei
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    type="button"
                    className="button"
                    onClick={() => onMenuLinkAdd?.(bot?.id)}
                    disabled={!bot?.id}
                  >
                    Legg til snarvei
                  </button>
                </div>
              </details>
            </div>
          </div>
        );
      }
      case 'footer': {
        const footer = config.theme.footer && typeof config.theme.footer === 'object' ? config.theme.footer : {};
        const nav = footer.nav && typeof footer.nav === 'object' ? footer.nav : {};
        const background = footer.background && typeof footer.background === 'object' ? footer.background : {};

        const footerStyle = footer.style === 'dock' ? 'dock' : 'minimal';
        const navMode = nav.mode === 'solid' || nav.mode === 'gradient' || nav.mode === 'glass' ? nav.mode : 'glass';
        const bgMode =
          background.mode === 'solid'
          || background.mode === 'gradient'
          || background.mode === 'fade'
          || background.mode === 'none'
            ? background.mode
            : 'fade';
        return (
          <div className="chat-builder-inspector-section">
            <h3>Footer</h3>
            <div className="chat-builder-fields">
              <details className="chat-builder-collapse" open>
                <summary>Type</summary>
                <div className="chat-builder-collapse-body">
                  <label className="chat-builder-field">
                    <span className="chat-builder-field-label">Footer-type</span>
                    <select
                      className="select"
                      value={footerStyle}
                      onChange={(event) => setThemeFooter({ style: event.target.value === 'dock' ? 'dock' : 'minimal' })}
                    >
                      <option value="minimal">Minimal</option>
                      <option value="dock">Glass dock</option>
                    </select>
                  </label>
                </div>
              </details>

              <details className="chat-builder-collapse" open>
                <summary>Navigasjonsbar</summary>
                <div className="chat-builder-collapse-body">
                  <div className="chat-builder-hint">
                    Farger redigeres i forhåndsvisningen.
                  </div>
                  <label className="chat-builder-field">
                    <span className="chat-builder-field-label">Bakgrunn</span>
                    <select
                      className="select"
                      value={navMode}
                      onChange={(event) => setThemeFooterNav({ mode: event.target.value })}
                    >
                      <option value="glass">Liquid glass</option>
                      <option value="solid">Solid</option>
                      <option value="gradient">Gradient</option>
                    </select>
                  </label>
                </div>
              </details>

              <details className="chat-builder-collapse" open>
                <summary>Bakgrunn</summary>
                <div className="chat-builder-collapse-body">
                  <div className="chat-builder-hint">
                    Farger redigeres i forhåndsvisningen.
                  </div>
                  <label className="chat-builder-field">
                    <span className="chat-builder-field-label">Stil</span>
                    <select
                      className="select"
                      value={bgMode}
                      onChange={(event) => setThemeFooterBg({ mode: event.target.value })}
                    >
                      <option value="none">Ingen</option>
                      <option value="fade">Fade</option>
                      <option value="solid">Solid</option>
                      <option value="gradient">Gradient</option>
                    </select>
                  </label>
                </div>
              </details>
            </div>
          </div>
        );
      }
      case 'advanced':
        return (
          <div className="chat-builder-inspector-section">
            <h3>Avansert</h3>
            <div className="chat-builder-actions">
              <button type="button" className="button button-primary" onClick={handleCopy}>
                Kopier JSON
              </button>
              <button type="button" className="button" onClick={() => setImportOpen((open) => !open)}>
                {importOpen ? 'Skjul import' : 'Importer JSON'}
              </button>
              <button type="button" className="button button-danger" onClick={handleReset}>
                Nullstill
              </button>
            </div>

            {importOpen && (
              <div className="chat-builder-import">
                <textarea
                  className="textarea"
                  rows={8}
                  value={importValue}
                  onChange={(event) => setImportValue(event.target.value)}
                  placeholder='Lim inn JSON med { "version": 1, ... }'
                />
                <div className="chat-builder-import-actions">
                  <button type="button" className="button button-primary" onClick={handleApplyImport}>
                    Bruk import
                  </button>
                  {importError && <span className="chat-builder-import-error">{importError}</span>}
                </div>
              </div>
            )}

            <div className="chat-builder-json">
              <h4>JSON</h4>
              <textarea className="textarea chat-builder-json-textarea" rows={16} readOnly value={jsonValue} />
              <span className="input-hint">
                Denne JSON-en er ment å lagres på boten (f.eks. <code>ui_config</code>) og sendes til ChatUI ved oppstart.
              </span>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  
  const renderColorPopover = () => {
    if (!activePopover) return null;

    const lockNotice = isThemeLocked ? (
      <div className="chat-builder-hint chat-builder-hint--warning">
        Fargene er låst av temaet <code>{currentPresetId}</code>. Velg <code>Tilpasset</code> i <code>Hurtigoppsett</code> for å redigere.
      </div>
    ) : null;

    switch (activePopover) {
      case 'header-colors': {
        const headerBackgroundMode = config.header.background?.type === 'gradient' ? 'gradient' : 'solid';

        return (
          <>
            <div className="chat-builder-popover-title">Header</div>
            <div className="chat-builder-popover-body">
              {lockNotice}
              <label className="chat-builder-field">
                <span className="chat-builder-field-label">Bakgrunn</span>
                <select
                  className="select"
                  value={headerBackgroundMode}
                  onChange={(event) =>
                    setHeaderBackground({ type: event.target.value === 'gradient' ? 'gradient' : 'solid' })}
                  disabled={isThemeLocked}
                >
                  <option value="solid">Solid</option>
                  <option value="gradient">Gradient</option>
                </select>
              </label>

              {headerBackgroundMode === 'solid' ? (
                <InlineColorField
                  label="Bakgrunnsfarge"
                  value={config.header.background.color}
                  onChange={(value) =>
                    setHeaderBackground({ color: normalizeHex(value, config.header.background.color) })}
                  disabled={isThemeLocked}
                />
              ) : (
                <>
                  <InlineColorField
                    label="Gradient start"
                    value={config.header.background.gradientStart}
                    onChange={(value) =>
                      setHeaderBackground({
                        gradientStart: normalizeHex(value, config.header.background.gradientStart),
                      })}
                    disabled={isThemeLocked}
                  />
                  <InlineColorField
                    label="Gradient slutt"
                    value={config.header.background.gradientEnd}
                    onChange={(value) =>
                      setHeaderBackground({
                        gradientEnd: normalizeHex(value, config.header.background.gradientEnd),
                      })}
                    disabled={isThemeLocked}
                  />
                  {showAdvanced && (
                    <label className="chat-builder-field">
                      <span className="chat-builder-field-label">Vinkel</span>
                      <input
                        className="input"
                        type="number"
                        min={0}
                        max={360}
                        value={config.header.background.angle}
                        onChange={(event) =>
                          setHeaderBackground({ angle: clampNumber(event.target.value, 0, 360, 135) })}
                        disabled={isThemeLocked}
                      />
                    </label>
                  )}
                </>
              )}
            </div>
          </>
        );
      }
      case 'background': {
        const bgType = config.theme.background?.type === 'solid' ? 'solid' : 'gradient';
        const bgEffect = String(config.theme.background?.effect || 'none');
        const bgModeRaw = bgEffect === 'floating-lines' ? 'floating-lines' : bgType;
        const usesFloatingLines = bgModeRaw === 'floating-lines';
        const bgMode = !showAdvanced && usesFloatingLines ? 'gradient' : bgModeRaw;

        return (
          <>
            <div className="chat-builder-popover-title">Bakgrunn</div>
            <div className="chat-builder-popover-body">
              {lockNotice}
              {!showAdvanced && usesFloatingLines && (
                <div className="chat-builder-hint chat-builder-hint--warning">
                  Avansert bakgrunnseffekt (Floating Lines) er aktiv. Slå på <strong>Avansert</strong> for å endre.
                </div>
              )}
              <label className="chat-builder-field">
                <span className="chat-builder-field-label">Stil</span>
                <select
                  className="select"
                  value={bgMode}
                  onChange={(event) => {
                    const mode = event.target.value;
                    if (mode === 'solid') {
                      const start = config.theme.background.start;
                      setThemeBackground({ type: 'solid', effect: 'none', start, end: start });
                      applyThemeColorPatch('gradientStart', start);
                      applyThemeColorPatch('gradientEnd', start);
                      return;
                    }
                    if (mode === 'floating-lines') {
                      setThemeBackground({ type: 'gradient', effect: 'floating-lines' });
                      return;
                    }
                    setThemeBackground({ type: 'gradient', effect: 'none' });
                  }}
                  disabled={isThemeLocked}
                >
                  <option value="solid">Solid</option>
                  <option value="gradient">Gradient</option>
                  {showAdvanced && <option value="floating-lines">Floating Lines</option>}
                </select>
              </label>

              {bgMode === 'solid' && (
                <InlineColorField
                  label="Farge"
                  value={config.theme.background.start}
                  onChange={(value) => {
                    const normalized = normalizeHex(value, config.theme.background.start);
                    setThemeBackground({ start: normalized, end: normalized });
                    applyThemeColorPatch('gradientStart', normalized);
                    applyThemeColorPatch('gradientEnd', normalized);
                  }}
                  disabled={isThemeLocked}
                />
              )}

              {bgMode !== 'solid' && (
                <>
                  <InlineColorField
                    label="Start"
                    value={config.theme.background.start}
                    onChange={(value) => {
                      const normalized = normalizeHex(value, config.theme.background.start);
                      setThemeBackground({ start: normalized });
                      applyThemeColorPatch('gradientStart', normalized);
                    }}
                    disabled={isThemeLocked}
                  />
                  <InlineColorField
                    label="Slutt"
                    value={config.theme.background.end}
                    onChange={(value) => {
                      const normalized = normalizeHex(value, config.theme.background.end);
                      setThemeBackground({ end: normalized });
                      applyThemeColorPatch('gradientEnd', normalized);
                    }}
                    disabled={isThemeLocked}
                  />

                  {bgMode === 'gradient' && showAdvanced && (
                    <label className="chat-builder-field">
                      <span className="chat-builder-field-label">Vinkel</span>
                      <input
                        className="input"
                        type="number"
                        min={0}
                        max={360}
                        value={config.theme.background.angle}
                        onChange={(event) =>
                          setThemeBackground({ angle: clampNumber(event.target.value, 0, 360, 135) })}
                        disabled={isThemeLocked}
                      />
                    </label>
                  )}

                  {bgMode === 'floating-lines' && showAdvanced && (
                    <div className="chat-builder-hint">
                      Floating Lines bruker <code>Footer</code> + start/slutt-fargene som gradient for linjene.
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        );
      }
      case 'surfaces': {
        return (
          <>
            <div className="chat-builder-popover-title">Kort og input</div>
            <div className="chat-builder-popover-body">
              {lockNotice}
              <InlineColorField
                label="Snarvei-kort"
                value={config.theme.surfaces.card}
                onChange={(value) => {
                  const normalized = normalizeHex(value, config.theme.surfaces.card);
                  setThemeSurfaces({ card: normalized });
                  applyThemeColorPatch('launcherColor', normalized);
                }}
                disabled={isThemeLocked}
              />
              <InlineColorField
                label="Inputfelt"
                value={config.theme.surfaces.input}
                onChange={(value) => {
                  const normalized = normalizeHex(value, config.theme.surfaces.input);
                  setThemeSurfaces({ input: normalized });
                  applyThemeColorPatch('inputBgColor', normalized);
                }}
                disabled={isThemeLocked}
              />
            </div>
          </>
        );
      }
      case 'footer-colors': {
        const footer = config.theme.footer && typeof config.theme.footer === 'object' ? config.theme.footer : {};
        const nav = footer.nav && typeof footer.nav === 'object' ? footer.nav : {};
        const background = footer.background && typeof footer.background === 'object' ? footer.background : {};
        const navMode = nav.mode === 'solid' || nav.mode === 'gradient' || nav.mode === 'glass' ? nav.mode : 'glass';
        const bgMode =
          background.mode === 'solid'
          || background.mode === 'gradient'
          || background.mode === 'fade'
          || background.mode === 'none'
            ? background.mode
            : 'fade';

        return (
          <>
            <div className="chat-builder-popover-title">Footer</div>
            <div className="chat-builder-popover-body">
              <div className="chat-builder-subsection">
                <div className="chat-builder-subsection-title">Navigasjonsbar</div>
                {navMode === 'glass' && (
                  <>
                    <InlineColorField
                      label="Tint"
                      value={nav.tint || '#111827'}
                      onChange={(value) => setThemeFooterNav({ tint: normalizeHex(value, nav.tint || '#111827') })}
                    />
                    <label className="chat-builder-field">
                      <span className="chat-builder-field-label">Opacity</span>
                      <div className="chat-builder-range-row">
                        <input
                          className="range"
                          type="range"
                          min={0.08}
                          max={0.92}
                          step={0.02}
                          value={nav.opacity ?? 0.72}
                          onChange={(event) =>
                            setThemeFooterNav({ opacity: clampNumber(event.target.value, 0.08, 0.92, 0.72) })}
                        />
                        <input
                          className="input"
                          type="number"
                          min={0.08}
                          max={0.92}
                          step={0.02}
                          value={nav.opacity ?? 0.72}
                          onChange={(event) =>
                            setThemeFooterNav({ opacity: clampNumber(event.target.value, 0.08, 0.92, 0.72) })}
                        />
                      </div>
                    </label>
                  </>
                )}

                {navMode === 'solid' && (
                  <InlineColorField
                    label="Bakgrunnsfarge"
                    value={nav.color || '#111827'}
                    onChange={(value) => setThemeFooterNav({ color: normalizeHex(value, nav.color || '#111827') })}
                  />
                )}

                {navMode === 'gradient' && (
                  <>
                    <InlineColorField
                      label="Start"
                      value={nav.gradientStart || '#111827'}
                      onChange={(value) =>
                        setThemeFooterNav({ gradientStart: normalizeHex(value, nav.gradientStart || '#111827') })}
                    />
                    <InlineColorField
                      label="Slutt"
                      value={nav.gradientEnd || '#0b1020'}
                      onChange={(value) =>
                        setThemeFooterNav({ gradientEnd: normalizeHex(value, nav.gradientEnd || '#0b1020') })}
                    />
                    <label className="chat-builder-field">
                      <span className="chat-builder-field-label">Vinkel</span>
                      <input
                        className="input"
                        type="number"
                        min={0}
                        max={360}
                        value={nav.angle ?? 135}
                        onChange={(event) =>
                          setThemeFooterNav({ angle: clampNumber(event.target.value, 0, 360, 135) })}
                      />
                    </label>
                  </>
                )}
              </div>

              <div className="chat-builder-subsection">
                <div className="chat-builder-subsection-title">Footer-bakgrunn</div>
                {bgMode === 'none' && (
                  <div className="chat-builder-muted">
                    Ingen bakgrunnsfarge. Velg stil i <strong>Footer</strong> for å aktivere farger.
                  </div>
                )}
                {(bgMode === 'fade' || bgMode === 'solid') && (
                  <InlineColorField
                    label="Farge"
                    value={background.color || config.theme.accent}
                    onChange={(value) =>
                      setThemeFooterBg({ color: normalizeHex(value, background.color || config.theme.accent) })}
                  />
                )}

                {bgMode === 'gradient' && (
                  <>
                    <InlineColorField
                      label="Start"
                      value={background.gradientStart || config.theme.accent}
                      onChange={(value) =>
                        setThemeFooterBg({
                          gradientStart: normalizeHex(value, background.gradientStart || config.theme.accent),
                        })}
                    />
                    <InlineColorField
                      label="Slutt"
                      value={background.gradientEnd || config.theme.background.end}
                      onChange={(value) =>
                        setThemeFooterBg({
                          gradientEnd: normalizeHex(value, background.gradientEnd || config.theme.background.end),
                        })}
                    />
                    <label className="chat-builder-field">
                      <span className="chat-builder-field-label">Vinkel</span>
                      <input
                        className="input"
                        type="number"
                        min={0}
                        max={360}
                        value={background.angle ?? 135}
                        onChange={(event) =>
                          setThemeFooterBg({ angle: clampNumber(event.target.value, 0, 360, 135) })}
                      />
                    </label>
                  </>
                )}
              </div>
            </div>
          </>
        );
      }
      default:
        return null;
    }
  };

  const isHeaderRegionSelected = activeSection === 'global' || activeSection === 'header';
  const isHomeRegionSelected = activeSection === 'home';
  const isMessagesPreview = normalizedPreviewView === 'messages';

  const compactHeaderContent = config.header.compact?.content || 'both';
  const homeHeaderContent = config.header.home?.content || 'logo';
  const previewHeaderContent = isChatPreview || previewHeaderMode === 'compact' ? compactHeaderContent : homeHeaderContent;

  const hasLogo = config.header.logo.mode !== 'none';
  const wantsTitle = previewHeaderContent === 'title' || previewHeaderContent === 'both' || !hasLogo;
  const showTitle = wantsTitle;
  const showSubtitle =
    !isChatPreview
    && previewHeaderMode === 'hero'
    && showTitle
    && config.header.subtitle.enabled
    && Boolean(config.header.subtitle.text);

  const previewLogoSize = previewHeaderMode === 'hero' ? config.header.home.logoSize : config.header.logo.size;
  const previewHeroExtraHeight = 44 + (showTitle || showSubtitle ? 34 : 0);
  const previewHeaderHeight = previewHeaderMode === 'hero'
    ? `${previewLogoSize + previewHeroExtraHeight}px`
    : `${Math.max(52, config.header.logo.size + 24)}px`;

  return (
	    <div className="chat-builder">
	      <div className="chat-builder-layout">
	        <div className="chat-builder-preview-column">
	          <div
	            className="chat-builder-preview-frame"
	            ref={previewFrameRef}
	            style={{
	              fontFamily: config.theme.typography?.fontFamily || DEFAULT_FONT_FAMILY,
	              fontVariantNumeric: config.theme.typography?.fontVariantNumeric || 'normal',
	            }}
	          >
	            <div className="chat-builder-preview-window-bg" style={homeBgStyle} aria-hidden="true" />
	            {homeHasFloatingLines && (
	              <div className="chat-builder-preview-bg-effect" aria-hidden="true">
	                <FloatingLinesPreview
	                  enabledWaves={['top', 'middle', 'bottom']}
	                  lineCount={[10, 15, 20]}
	                  lineDistance={[8, 6, 4]}
	                  animationSpeed={1}
	                  interactive={false}
	                  parallax={false}
	                  mixBlendMode="screen"
	                  linesGradient={[config.theme.accent, config.theme.background.start, config.theme.background.end]}
	                />
	              </div>
	            )}
	            <div className="chat-builder-preview-overlay" aria-label="Design quick edit">
	              <button
	                type="button"
	                ref={headerChipRef}
	                className={[
	                  'chat-builder-preview-chip',
	                  'chat-builder-preview-chip--header',
	                  activePopover === 'header-colors' ? 'is-active' : '',
	                ].filter(Boolean).join(' ')}
	                onClick={(event) => {
	                  event.stopPropagation();
	                  openPopover('header-colors', event);
	                }}
	                aria-pressed={activePopover === 'header-colors'}
	              >
	                Header
	              </button>
	              <button
	                type="button"
	                ref={backgroundChipRef}
	                className={[
	                  'chat-builder-preview-chip',
	                  'chat-builder-preview-chip--background',
	                  activePopover === 'background' ? 'is-active' : '',
	                ].filter(Boolean).join(' ')}
	                onClick={(event) => {
	                  event.stopPropagation();
	                  openPopover('background', event);
	                }}
	                aria-pressed={activePopover === 'background'}
	              >
	                Bakgrunn
	              </button>
              <button
                type="button"
                ref={surfacesChipRef}
                className={[
                  'chat-builder-preview-chip',
                  'chat-builder-preview-chip--surfaces',
                  activePopover === 'surfaces' ? 'is-active' : '',
                ].filter(Boolean).join(' ')}
                onClick={(event) => {
                  event.stopPropagation();
                  openPopover('surfaces', event);
                }}
                aria-pressed={activePopover === 'surfaces'}
              >
                Kort & input
              </button>
              <button
                type="button"
                ref={footerChipRef}
                className={[
                  'chat-builder-preview-chip',
                  'chat-builder-preview-chip--footer',
                  activePopover === 'footer-colors' ? 'is-active' : '',
                ].filter(Boolean).join(' ')}
                onClick={(event) => {
                  event.stopPropagation();
                  openPopover('footer-colors', event);
                }}
                aria-pressed={activePopover === 'footer-colors'}
              >
                Footer
              </button>

	              {activePopover && (
	                <div
	                  className="chat-builder-popover"
	                  ref={popoverContainerRef}
	                  style={popoverStyle || { top: 16, left: 16 }}
	                  role="dialog"
	                  aria-label="Fargevalg"
	                >
	                  {renderColorPopover()}
	                </div>
	              )}
	            </div>
	            <div
	              className={[
	                'chat-builder-preview-header',
	                previewHeaderMode === 'hero' ? 'chat-builder-preview-header--hero' : `chat-builder-preview-header--${config.header.template}`,
	                previewHeaderMode === 'hero' && config.header.home.align === 'center' ? 'is-center' : '',
	                isHeaderRegionSelected ? 'is-selected' : '',
	              ].filter(Boolean).join(' ')}
	              ref={previewHeaderRef}
	              style={{
	                ...previewHeaderBgStyle,
	                ...headerShapeStyle,
	                height: previewHeaderHeight,
	                display: 'block',
	                padding: 0,
	                color: previewHeaderTextColor,
	                borderBottom: headerBorderEnabled ? `1px solid ${config.header.border.color}` : 'none',
	              }}
	              onClick={(event) => {
	                // Don't steal clicks from actual buttons in the header.
	                if (event.target && event.target.closest && event.target.closest('button')) return;
	                selectInspectorSection('header');
	              }}
	            >
              <ChatHeader
                key={normalizedPreviewView}
                activeView={normalizedPreviewView}
                uiConfig={config}
                botLogoUrl={bot?.logoPreviewUrl || null}
                isInline
	                onBack={() => setPreviewView('home')}
	                style={{
	                  margin: 0,
	                  padding: '0 12px',
	                  height: '100%',
	                  width: '100%',
	                }}
	              />
	            </div>
	
	            <div
	              className={`chat-builder-preview-home ${isHomeRegionSelected ? 'is-selected' : ''}`}
	              onClick={() => selectInspectorSection('home')}
	              role="button"
	              tabIndex={0}
	              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  selectInspectorSection('home');
                }
	              }}
	            >
	              <div className="chat-builder-preview-home-inner" style={{ color: homeTextColor }}>
	                {!isChatPreview && !isMessagesPreview && (
	                  <>
	                    <div
	                      className={[
	                        'chat-builder-preview-home-top',
	                        config.header.home.align === 'center' ? 'is-center' : '',
	                      ].filter(Boolean).join(' ')}
	                    >
	                      <div className="chat-builder-preview-home-title">{homeIntroTitle}</div>
	                      <div className="chat-builder-preview-home-subtitle">{homeIntroSubtitle}</div>
	
	                      <div
	                        className="chat-builder-preview-composer"
	                        style={{
	                          backgroundColor: composerInputBg,
	                          border: composerIsLight ? '1px solid rgba(0,0,0,0.12)' : '1px solid rgba(255,255,255,0.15)',
	                        }}
	                      >
	                        <textarea
	                          className="chat-builder-preview-composer-input"
	                          rows={1}
	                          value=""
	                          placeholder="Skriv din melding"
	                          readOnly
	                          style={{ color: composerText, '--placeholder-color': withAlpha(composerText, 0.55) }}
	                          aria-label="Composer preview"
	                        />
	                        <button
	                          type="button"
	                          className="chat-builder-preview-composer-send"
	                          style={{ backgroundColor: startButtonBg, color: startButtonText }}
	                          aria-label="Start chat"
	                          onClick={(event) => {
	                            event.preventDefault();
	                            event.stopPropagation();
	                            setPreviewView('chat');
	                            selectInspectorSection('home');
	                          }}
	                        >
	                          <TbSend size={18} />
	                        </button>
	                      </div>
	                    </div>
	
	                    <div className="chat-builder-preview-shortcuts">
	                      {previewMenuLinks.length > 0 ? (
	                        <div className="chat-builder-preview-shortcuts-panel">
	                          {previewMenuLinks.map((link, index) => (
	                            <button
	                              key={link.key}
	                              type="button"
	                              className={`chat-builder-preview-shortcut ${index < previewMenuLinks.length - 1 ? 'has-divider' : ''}`}
	                              onClick={(event) => {
	                                event.preventDefault();
	                                event.stopPropagation();
	                                selectInspectorSection('home');
	                              }}
	                            >
	                              <span className="chat-builder-preview-shortcut-icon" aria-hidden="true">
	                                {link.icon || '💬'}
	                              </span>
	                              <span className="chat-builder-preview-shortcut-text">
	                                <span className="chat-builder-preview-shortcut-title">{link.label}</span>
	                                {link.description && (
	                                  <span className="chat-builder-preview-shortcut-desc">{link.description}</span>
	                                )}
	                              </span>
	                            </button>
	                          ))}
	                        </div>
	                      ) : (
	                        <div className="chat-builder-preview-menu-empty">
	                          Ingen snarveier. Legg til snarveier under <strong>Hjem</strong>.
	                        </div>
	                      )}
	                    </div>
	                  </>
	                )}

	                {isMessagesPreview && (
	                  <div className="chat-builder-preview-messages">
	                    <div className="chat-builder-preview-home-title">Meldinger</div>
	                    <div className="chat-builder-preview-home-subtitle">Eksempel på tidligere samtaler</div>
	                    <div className="chat-builder-preview-shortcuts-panel">
	                      {['Ordrestatus', 'Retur av varer', 'Åpningstider'].map((label) => (
	                        <button
	                          key={label}
	                          type="button"
	                          className="chat-builder-preview-shortcut"
	                          onClick={(event) => {
	                            event.preventDefault();
	                            event.stopPropagation();
	                            setPreviewView('chat');
	                          }}
	                        >
	                          <span className="chat-builder-preview-shortcut-icon" aria-hidden="true">
	                            <BiSolidMessageSquareDetail size={16} />
	                          </span>
	                          <span className="chat-builder-preview-shortcut-text">
	                            <span className="chat-builder-preview-shortcut-title">{label}</span>
	                            <span className="chat-builder-preview-shortcut-desc">Trykk for å åpne</span>
	                          </span>
	                        </button>
	                      ))}
	                    </div>
	                  </div>
	                )}

	                {isChatPreview && (
	                  <div className="chat-builder-preview-chat">
	                    <div className="chat-builder-preview-chat-messages">
	                      <div className="chat-builder-preview-bubble chat-builder-preview-bubble--bot">
	                        Hei! Hvordan kan jeg hjelpe deg?
	                      </div>
	                      <div className="chat-builder-preview-bubble chat-builder-preview-bubble--user">
	                        Jeg vil bestille pizza 🍕
	                      </div>
	                      <div className="chat-builder-preview-bubble chat-builder-preview-bubble--bot">
	                        Klart! Hvilken størrelse ønsker du?
	                      </div>
	                    </div>
	
	                    <div
	                      className="chat-builder-preview-composer chat-builder-preview-composer--chat"
	                      style={{
	                        backgroundColor: composerInputBg,
	                        border: composerIsLight ? '1px solid rgba(0,0,0,0.12)' : '1px solid rgba(255,255,255,0.15)',
	                      }}
	                    >
	                      <textarea
	                        className="chat-builder-preview-composer-input"
	                        rows={1}
	                        value=""
	                        placeholder="Skriv din melding"
	                        readOnly
	                        style={{ color: composerText, '--placeholder-color': withAlpha(composerText, 0.55) }}
	                        aria-label="Composer preview"
	                      />
	                      <button
	                        type="button"
	                        className="chat-builder-preview-composer-send"
	                        style={{ backgroundColor: startButtonBg, color: startButtonText }}
	                        aria-label="Send"
	                        onClick={(event) => {
	                          event.preventDefault();
	                          event.stopPropagation();
	                        }}
	                      >
	                        <TbSend size={18} />
	                      </button>
	                    </div>
	                  </div>
	                )}
	              </div>
            </div>

	            <div
	              className={[
	                'chat-builder-preview-footer',
	                footerVariant === 'dock' ? 'is-dock' : 'is-minimal',
	              ].join(' ')}
	              style={{ padding: 0, gap: 0 }}
	              onClick={(event) => {
	                // Don't steal clicks from the footer nav buttons.
	                if (event.target && event.target.closest && event.target.closest('button')) return;
	                event.preventDefault();
	                event.stopPropagation();
	                selectInspectorSection('footer');
	              }}
	              role="button"
	              tabIndex={0}
	              onKeyDown={(event) => {
	                if (event.key === 'Enter' || event.key === ' ') {
	                  event.preventDefault();
	                  selectInspectorSection('footer');
	                }
	              }}
	            >
	              <ChatFooter
	                activeView={normalizedPreviewView}
	                uiConfig={config}
	                isKeyboardOpen={false}
	                onNavigate={(view) => setPreviewView(view)}
	              />
	            </div>
          </div>
        </div>

        <aside className="chat-builder-inspector" aria-label="Design">
          <div className="chat-builder-inspector-nav">
            <div className="chat-builder-inspector-tabs" role="tablist" aria-label="Seksjoner">
              {INSPECTOR_SECTIONS.map((section) => (
                <button
                  key={section.id}
                  type="button"
                  role="tab"
                  aria-selected={activeSection === section.id}
                  className={[
                    'chat-builder-inspector-tab',
                    activeSection === section.id ? 'is-active' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => selectInspectorSection(section.id)}
                >
                  {section.label}
                </button>
              ))}
            </div>
          </div>
          <div className="chat-builder-inspector-body">
            {renderInspectorPanel()}
            <div className="chat-builder-inspector-tip">
              Tips: klikk på headeren, hjem-skjermen eller footeren i forhåndsvisningen for å åpne riktig panel.
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

export default ChatBuilderEditor;
